import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { hasAdminRights } from '../utils/roleUtils';
import ConfirmationModal from '../components/ConfirmationModal';
import EventCard from '../components/EventCard';
import {
  UserGroupIcon,
  CreditCardIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  TrashIcon,
  EyeIcon,
  CalendarIcon,
  CurrencyEuroIcon,
  ClockIcon,
  TicketIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowRightIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';

const CARD_BG = '#1c2432';

interface GuestCardType {
  id: number;
  name: string;
  total_entries: number;
  price: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

interface Guest {
  mg_nr: string;
  mitglied_nr: string;
  name: string;
  vorname: string;
  email: string;
  tel_nummer?: string;
  eintritt?: string;
  last_login: string | null;
  last_booking: string | null;
  cards: any[];
}

interface GuestCard {
  id: number;
  guest_card_type_id: number;
  member_id: string;
  remaining_entries: number;
  total_entries?: number;
  purchase_date: string;
  expiry_date: string;
  is_active?: boolean;
  bezahlt?: boolean;
  bezahlt_am?: string | null;
  type?: GuestCardType;
  card_name?: string;
}

interface Booking {
  id?: number;
  termin_booking_id?: number;
  kalender_id: number;  // This is the event_id needed for cancellation
  card_id?: number;
  cardId?: number;
  guest_card_id?: number;
  booking_date: string;
  title: string;
  start: string;
  start_time: string;
  end_time?: string;
  creator_id?: string;
  creator_name?: string;
  creator_image?: string;
  creator_image_updated?: string | number;
  cotrainer_id?: string;
  cotrainer_name?: string;
  cotrainer_image?: string;
  cotrainer_image_updated?: string | number;
  participantCount?: number;
  max_teilnehmer?: number;
  farbe?: string;
  unique_id?: string;
  cardName?: string;
}

interface Stats {
  totalRevenue: number;
  paidRevenue: number;
  totalSoldCards: number;
  activeGuestCards: number;
  totalBookedTickets: number;
  bookedThisMonth: number;
  bookedLastMonth: number;
  soldThisMonth: number;
  soldLastMonth: number;
  avgCardsPerGuest: string;
  totalEvents: number;
  bookedEvents: number;
  availableYears: number[];
  cardTypes?: any;
  revenueByCardType?: any;
  monthlyPurchasesByType?: any;
  monthlyBookingsByType?: any;
  monthlyTotalPurchases?: any;
  topMonthsByRevenue?: any[];
  allMonths?: string[];
  unpaidBookingMembers?: any[];
}

interface ExtendedStats {
  timeSeries: Array<{
    date: string;
    purchases: { count: number; revenue: number; unique_buyers: number };
    payments: { count: number; revenue: number; avg_delay: number | null };
    bookings: { count: number; unique_users: number; unique_events: number };
    cumulative: {
      purchases: number;
      revenue: number;
      payments: number;
      bookings: number;
    };
  }>;
  monthly: any[];
  cardTypes: any[];
  paymentAnalysis: any[];
  utilization: any[];
  retention: any[];
  year: number;
}

const GuestCardManagement: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);
  const [stats, setStats] = useState<Stats | null>(null);
  const [extendedStats, setExtendedStats] = useState<ExtendedStats | null>(null);
  const [recentBookings, setRecentBookings] = useState<any[]>([]);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [filteredGuests, setFilteredGuests] = useState<Guest[]>([]);
  const [cardTypes, setCardTypes] = useState<GuestCardType[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyWithCards, setShowOnlyWithCards] = useState(true);
  const [loading, setLoading] = useState(true);
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [, setTouchEnd] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragDistance, setDragDistance] = useState(0);
  const [showCreateCardModal, setShowCreateCardModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState<GuestCard | null>(null);
  const [guestCards, setGuestCards] = useState<GuestCard[]>([]);
  const [sortField, setSortField] = useState<'name' | 'last_login' | 'last_booking'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [activeTab, setActiveTab] = useState<'cards' | 'history' | 'stats'>('cards');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'low' | 'expired'>('all');
  const [hasGaestekartenKonto, setHasGaestekartenKonto] = useState<boolean | null>(null);

  const [newCardType, setNewCardType] = useState({
    name: '',
    total_entries: '',
    price: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    unlimited: true,
  });

  const [paymentDetails, setPaymentDetails] = useState({
    bezahlt_am: new Date().toISOString().split('T')[0],
    document: null as File | null,
  });

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'warning' | 'error' | 'question' | 'info';
    title: string;
    message: string;
    variant?: 'single' | 'two';
    onConfirm?: () => void;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });

  const hasAccess = hasAdminRights(user);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/guest-cards/stats');
      if (response.data.success && response.data.stats) {
        setStats(response.data.stats);
      }
    } catch (error: any) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch extended statistics for charts
  const fetchExtendedStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/guest-cards/stats/extended');
      if (response.data.success && response.data.data) {
        setExtendedStats(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching extended stats:', error);
    }
  }, []);

  // Fetch recent bookings across ALL guest cards (for Verlauf-Timeline)
  const fetchRecentBookings = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/guest-cards/recent-bookings?limit=100');
      if (response.data.success) {
        setRecentBookings(response.data.bookings || []);
      }
    } catch (error: any) {
      console.error('Error fetching recent bookings:', error);
    }
  }, []);

  // Fetch guests with cards
  const fetchGuests = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/guest-cards/guests-with-cards');
      if (response.data.success) {
        setGuests(response.data.guests);
      }
    } catch (error) {
      console.error('Error fetching guests:', error);
    }
  }, []);

  // Fetch card types
  const fetchCardTypes = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/guest-cards/types/all');
      if (response.data) {
        setCardTypes(response.data.cardTypes || []);
      }
    } catch (error) {
      console.error('Error fetching card types:', error);
    }
  }, []);

  // Fetch guest details
  const fetchGuestDetails = useCallback(async (mg_nr: string) => {
    console.log('=== fetchGuestDetails ===');
    console.log('Fetching details for mg_nr:', mg_nr);
    console.log('Current guestCards state:', guestCards);

    try {
      const cardsResponse = await apiClient.get(`/api/guest-cards/guest-cards?member_id=${mg_nr}`);
      console.log('API Response:', cardsResponse.data);

      if (cardsResponse.data.success) {
        const cards = cardsResponse.data.cards || [];
        console.log('Cards from API:', cards);

        // Preserve card_name from current guestCards state if not in response
        const cardsWithNames = cards.map((card: any) => {
          // Try to find the existing card name from current state
          const existingCard = guestCards.find(gc => gc.id === card.id);
          console.log(`Card ${card.id}:`, {
            'from_api_card_name': card.card_name,
            'from_api_name': card.name,  // Log the 'name' field
            'existing_card': existingCard,
            'existing_card_name': existingCard?.card_name,
            'card_type': card.type,
            'card_type_name': card.type?.name
          });

          const finalCardName = card.card_name ||
                              card.name ||  // API gibt "name" statt "card_name" zurück!
                              existingCard?.card_name ||
                              card.type?.name ||
                              'Gästekarte';

          console.log(`Final card_name for card ${card.id}:`, finalCardName);

          return {
            ...card,
            card_name: finalCardName
          };
        });

        console.log('Cards with names:', cardsWithNames);
        setGuestCards(cardsWithNames);

        // Update selectedGuest with the detailed card data including bookings
        setSelectedGuest(prev => ({
          ...prev!,
          cards: cardsWithNames
        }));

      } else {
        // Handle non-success response
        setGuestCards([]);
      }
    } catch (error: any) {
      console.error('Error fetching guest details:', error);
      // Set empty data on error to show modal with no cards
      setGuestCards([]);

      // Only show error message for non-401 errors (401 is handled by apiClient interceptor)
      if (error.response?.status !== 401) {
        // You could add a notification here if needed
        console.error('Failed to load guest details. Server returned:', error.response?.status);
      }
    }
  }, [guestCards]);

  // Apply filters and sorting
  const applyFiltersAndSort = useCallback(() => {
    let result = [...guests];

    // Filter by cards
    if (showOnlyWithCards) {
      result = result.filter(g => Array.isArray(g.cards) && g.cards.length > 0);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        g =>
          (g.name && g.name.toLowerCase().includes(term)) ||
          (g.vorname && g.vorname.toLowerCase().includes(term)) ||
          (g.email && g.email.toLowerCase().includes(term))
      );
    }

    // Sort
    result.sort((a, b) => {
      let valueA: any = a[sortField];
      let valueB: any = b[sortField];

      if (sortField === 'name') {
        valueA = `${a.vorname} ${a.name}`.toLowerCase();
        valueB = `${b.vorname} ${b.name}`.toLowerCase();
      } else if (sortField === 'last_login' || sortField === 'last_booking') {
        valueA = valueA ? new Date(valueA).getTime() : 0;
        valueB = valueB ? new Date(valueB).getTime() : 0;
      }

      if (valueA === valueB) return 0;
      if (valueA === 0) return 1;
      if (valueB === 0) return -1;

      return sortOrder === 'asc'
        ? (valueA < valueB ? -1 : 1)
        : (valueA > valueB ? -1 : 1);
    });

    setFilteredGuests(result);
  }, [guests, searchTerm, sortField, sortOrder, showOnlyWithCards]);

  // Check if a Gästekarten account is configured
  const checkGaestekartenKonto = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/finance/accounts');
      const accounts = response.data || [];
      const hasKonto = accounts.some((acc: any) => acc.ist_gaestekarten_konto === 1);
      setHasGaestekartenKonto(hasKonto);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      setHasGaestekartenKonto(false);
    }
  }, []);

  useEffect(() => {
    if (hasAccess) {
      setLoading(true);
      Promise.all([
        fetchStats(),
        fetchGuests(),
        fetchCardTypes(),
        fetchExtendedStats(),
        fetchRecentBookings(),
        checkGaestekartenKonto()
      ])
        .finally(() => setLoading(false));
    }
  }, [hasAccess, fetchStats, fetchGuests, fetchCardTypes, fetchExtendedStats, fetchRecentBookings, checkGaestekartenKonto]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [applyFiltersAndSort]);

  // Handle modal animation
  useEffect(() => {
    if (showDetailsModal) {
      setTimeout(() => setIsModalVisible(true), 10);
    } else {
      setIsModalVisible(false);
    }
  }, [showDetailsModal]);

  const handleSort = (field: 'name' | 'last_login' | 'last_booking') => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Check if cancellation is allowed (24h after event)
  const getCancellationStatus = (booking: any) => {
    const eventDateTime = new Date(`${booking.start}T${booking.start_time || '00:00'}`);
    const now = new Date();
    const hoursSinceEvent = (now.getTime() - eventDateTime.getTime()) / (1000 * 60 * 60);

    // Allow cancellation if event is in future OR within 24h after event
    const canCancel = eventDateTime > now || hoursSinceEvent <= 24;

    return {
      allowed: canCancel,
      reason: canCancel ? undefined : 'Stornierung nur bis 24h nach dem Event möglich'
    };
  };


  const handleViewDetails = (guest: Guest) => {
    console.log('=== handleViewDetails ===');
    console.log('Guest data:', guest);
    console.log('Guest cards:', guest.cards);

    setSelectedGuest(guest);

    // Use existing cards data if available as initial data
    if (guest.cards && Array.isArray(guest.cards)) {
      // Set initial data from guest object
      const mappedCards = guest.cards.map((card: any) => {
        console.log('Mapping card:', card);
        console.log('card.card_name:', card.card_name);
        console.log('card.type:', card.type);

        const mappedCard = {
          ...card,
          member_id: guest.mg_nr,
          // Ensure required fields have defaults
          remaining_entries: card.remaining_entries || 0,
          total_entries: card.total_entries || card.type?.total_entries || 0,
          purchase_date: card.purchase_date || card.created_at || '',
          expiry_date: card.expiry_date || '',
          is_active: card.is_active !== undefined ? card.is_active : true,
          bezahlt: card.bezahlt || false,
          bezahlt_am: card.bezahlt_am || null,
          card_name: card.card_name || card.type?.name || 'Gästekarte',
        };

        console.log('Mapped card result:', mappedCard);
        return mappedCard;
      });

      console.log('All mapped cards:', mappedCards);
      setGuestCards(mappedCards);
    } else {
      setGuestCards([]);
    }

    // Show modal immediately
    setShowDetailsModal(true);

    // Fetch details after a small delay to ensure state is set
    setTimeout(() => {
      fetchGuestDetails(guest.mg_nr);
    }, 100);
  };


  const handleCreateCardType = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const cardData = {
        ...newCardType,
        end_date: newCardType.unlimited ? null : newCardType.end_date,
        is_active: true  // Stelle sicher, dass neue Karten aktiv sind
      };
      await apiClient.post('/api/guest-cards/types', cardData);
      setShowCreateCardModal(false);
      setNewCardType({
        name: '',
        total_entries: '',
        price: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        unlimited: true,
      });
      fetchCardTypes();
    } catch (error) {
      console.error('Error creating card type:', error);
    }
  };

  const handleDeactivateCardType = (cardType: GuestCardType) => {
    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: 'Kartentyp deaktivieren',
      message: `Möchten Sie den Kartentyp "${cardType.name}" wirklich deaktivieren?`,
      variant: 'two',
      onConfirm: () => handleDeactivateConfirm(cardType.id)
    });
  };

  const handleDeactivateConfirm = async (cardTypeId: number) => {
    try {
      await apiClient.put(`/api/guest-cards/types/deactivate/${cardTypeId}`);
      fetchCardTypes();
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Kartentyp wurde erfolgreich deaktiviert.',
        variant: 'single'
      });
    } catch (error) {
      console.error('Error deactivating card type:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Fehler beim Deaktivieren des Kartentyps.',
        variant: 'single'
      });
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedCard) return;

    try {
      const formData = new FormData();
      formData.append('card_id', selectedCard.id.toString());
      formData.append('bezahlt_am', paymentDetails.bezahlt_am);
      if (paymentDetails.document) {
        formData.append('document', paymentDetails.document);
      }

      const response = await apiClient.post('/api/guest-cards/mark-paid', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        if (selectedGuest) {
          fetchGuestDetails(selectedGuest.mg_nr);
        }
        setShowPaymentModal(false);
        setSelectedCard(null);
        setPaymentDetails({
          bezahlt_am: new Date().toISOString().split('T')[0],
          document: null,
        });
        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Die Karte wurde als bezahlt markiert.',
          variant: 'single'
        });
      }
    } catch (error) {
      console.error('Error marking card as paid:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Die Karte konnte nicht als bezahlt markiert werden.',
        variant: 'single'
      });
    }
  };

  // Bezahlt-Markierung stornieren (Karte zurück auf unbezahlt)
  const handleUnmarkPaid = (card: GuestCard) => {
    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: 'Bezahlt-Markierung stornieren',
      message: `Möchten Sie die Bezahlt-Markierung für die Karte "${card.card_name || 'Gästekarte'}" wirklich stornieren? Die zugehörige Finanzbuchung wird gelöscht.`,
      variant: 'two',
      onConfirm: async () => {
        try {
          const response = await apiClient.post('/api/guest-cards/unmark-paid', {
            card_id: card.id,
          });
          if (response.data.success) {
            if (selectedGuest) {
              fetchGuestDetails(selectedGuest.mg_nr);
            }
            // Komplett neu laden, da Karten-Status sich geändert hat
            try {
              const refreshed = await apiClient.get('/api/guest-cards/guests-with-cards');
              if (refreshed.data?.guests) {
                setGuests(refreshed.data.guests);
              }
            } catch {}
            setConfirmModal({
              isOpen: true,
              type: 'success',
              title: 'Storniert',
              message: 'Die Bezahlt-Markierung wurde entfernt und die Buchung gelöscht.',
              variant: 'single'
            });
          }
        } catch (error) {
          console.error('Error unmarking card as paid:', error);
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: 'Die Bezahlt-Markierung konnte nicht storniert werden.',
            variant: 'single'
          });
        }
      },
    });
  };

  // Einzelne unbezahlte Karte löschen
  const handleDeleteCard = (card: GuestCard) => {
    const usedTickets = (card.total_entries || 0) - (card.remaining_entries || 0);

    if (usedTickets > 0) {
      // Karte wurde genutzt - doppelte Bestätigung
      setConfirmModal({
        isOpen: true,
        type: 'warning',
        title: 'Karte mit Buchungen löschen',
        message: `Diese Karte "${card.card_name || 'Gästekarte'}" hat ${usedTickets} genutzte Buchung(en). Soll die Karte trotzdem gelöscht werden? Alle zugehörigen Buchungen werden ebenfalls entfernt.`,
        variant: 'two',
        onConfirm: () => handleDeleteCardConfirm(card)
      });
    } else {
      setConfirmModal({
        isOpen: true,
        type: 'warning',
        title: 'Karte löschen',
        message: `Möchten Sie die unbezahlte Karte "${card.card_name || 'Gästekarte'}" wirklich löschen?`,
        variant: 'two',
        onConfirm: () => handleDeleteCardConfirm(card)
      });
    }
  };

  const handleDeleteCardConfirm = async (card: GuestCard) => {
    try {
      const response = await apiClient.delete(`/api/guest-cards/card/${card.id}`);
      if (response.data.success) {
        // Karte aus lokaler Liste entfernen
        setGuestCards(prev => prev.filter(c => c.id !== card.id));
        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Erfolgreich',
          message: response.data.message,
          variant: 'single'
        });
        fetchGuests();
        fetchStats();
      }
    } catch (error: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: error.response?.data?.message || 'Fehler beim Löschen der Karte.',
        variant: 'single'
      });
    }
  };

  // Gast-Mitglied löschen (nur wenn keine unbezahlten Karten mehr)
  const handleDeleteGuest = () => {
    if (!selectedGuest) return;

    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: 'Gast löschen',
      message: `Möchten Sie "${selectedGuest.vorname} ${selectedGuest.name}" wirklich als Gast löschen? Das Mitglied wird als gelöscht markiert und die E-Mail-Adresse entfernt.`,
      variant: 'two',
      onConfirm: () => handleDeleteGuestConfirm()
    });
  };

  const handleDeleteGuestConfirm = async () => {
    if (!selectedGuest) return;
    try {
      const response = await apiClient.delete(`/api/guest-cards/delete-guest/${selectedGuest.mg_nr}`);
      if (response.data.success) {
        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Erfolgreich',
          message: response.data.message,
          variant: 'single'
        });
        setIsModalVisible(false);
        setTimeout(() => setShowDetailsModal(false), 300);
        setSelectedGuest(null);
        fetchGuests();
        fetchStats();
      }
    } catch (error: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: error.response?.data?.message || 'Fehler beim Löschen des Gastes.',
        variant: 'single'
      });
    }
  };

  const handleCancelBooking = (booking: any) => {
    // First check cancellation status with 24h rule
    const cancellationStatus = getCancellationStatus(booking);
    if (!cancellationStatus.allowed) {
      setConfirmModal({
        isOpen: true,
        type: 'warning',
        title: 'Stornierung nicht möglich',
        message: cancellationStatus.reason || 'Diese Buchung kann nicht mehr storniert werden.',
        variant: 'single'
      });
      return;
    }

    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: 'Buchung stornieren',
      message: `Möchten Sie die Buchung für "${booking.title}" wirklich stornieren?`,
      variant: 'two',
      onConfirm: () => handleCancelBookingConfirm(booking)
    });
  };

  const handleCancelBookingConfirm = async (booking: Booking) => {
    try {
      const memberId = selectedGuest?.mg_nr;
      let cardId = booking.card_id || booking.guest_card_id;

      // If no card_id in booking, try to find it from guest cards
      if (!cardId && guestCards.length > 0) {
        // Try to find a card that could have been used for this booking
        cardId = guestCards[0].id;
      }

      if (!memberId || !cardId || !booking.kalender_id) {
        console.error('Missing required data for cancellation:', { memberId, cardId, kalenderId: booking.kalender_id });
        setConfirmModal({
          isOpen: true,
          type: 'error',
          title: 'Fehler',
          message: 'Fehlende Daten für die Stornierung. Bitte versuchen Sie es später erneut.',
          variant: 'single'
        });
        return;
      }

      // Use the correct API endpoint from reference implementation
      await apiClient.delete(
        `/api/members/cancel-event/${booking.kalender_id}?member_id=${memberId}&card_id=${cardId}`
      );

      if (selectedGuest) {
        fetchGuestDetails(selectedGuest.mg_nr);
      }

      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Buchung wurde erfolgreich storniert.',
        variant: 'single'
      });
    } catch (error) {
      console.error('Error canceling booking:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Fehler beim Stornieren der Buchung.',
        variant: 'single'
      });
    }
  };

  const getInactivityStatus = (guest: Guest) => {
    const lastActivity = guest.last_login || guest.last_booking;
    if (!lastActivity) {
      return { status: 'never', label: 'Nie aktiv' };
    }

    const lastActivityDate = new Date(lastActivity);
    const now = new Date();
    const daysInactive = Math.floor((now.getTime() - lastActivityDate.getTime()) / (1000 * 60 * 60 * 24));

    if (daysInactive <= 30) {
      return { status: 'active', label: `Vor ${daysInactive} ${daysInactive === 1 ? 'Tag' : 'Tagen'}` };
    } else if (daysInactive <= 90) {
      return { status: 'returning', label: `Vor ${daysInactive} Tagen` };
    } else {
      return { status: 'inactive', label: `Vor ${daysInactive} Tagen` };
    }
  };

  const formatCurrency = (amount: number) => {
    const formatted = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);

    // Remove ,00 for whole numbers
    return formatted.replace(',00', '');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '...';

    // Handle invalid dates
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '...';
    }

    return date.toLocaleDateString('de-DE');
  };

  // ============================================================
  // DERIVED STATE: flat list of all cards across all guests
  // ============================================================
  const allCards = useMemo(() => {
    type FlatCard = {
      id: number;
      cardNumber: string;
      cardName: string;
      remaining: number;
      total: number;
      pct: number;
      isExpired: boolean;
      expiryDate: string;
      purchaseDate: string;
      bezahlt: boolean;
      bezahlt_am: string | null;
      ownerName: string;
      ownerInitials: string;
      ownerMgNr: string;
      ownerMitgliedNr: string;
      guest: Guest;
      raw: any;
    };
    const list: FlatCard[] = [];
    guests.forEach((g) => {
      const cards = Array.isArray(g.cards) ? g.cards : [];
      cards.forEach((c: any) => {
        const total = c.total_entries || c.type?.total_entries || 0;
        const remaining = c.remaining_entries ?? 0;
        const pct = total > 0 ? (remaining / total) * 100 : 0;
        const expiry = c.expiry_date || '';
        const isExpired = !!(expiry && !isNaN(new Date(expiry).getTime()) && new Date(expiry) < new Date());
        const initials = `${(g.vorname || ' ').charAt(0)}${(g.name || ' ').charAt(0)}`.toUpperCase();
        list.push({
          id: c.id,
          cardNumber: String(c.id),
          cardName: c.card_name || c.type?.name || c.name || 'Gästekarte',
          remaining,
          total,
          pct,
          isExpired,
          expiryDate: expiry,
          purchaseDate: c.purchase_date || c.created_at || '',
          bezahlt: !!c.bezahlt,
          bezahlt_am: c.bezahlt_am || null,
          ownerName: `${g.vorname || ''} ${g.name || ''}`.trim(),
          ownerInitials: initials,
          ownerMgNr: g.mg_nr,
          ownerMitgliedNr: g.mitglied_nr || g.mg_nr,
          guest: g,
          raw: c,
        });
      });
    });
    return list;
  }, [guests]);

  const cardCounts = useMemo(() => {
    const total = allCards.length;
    const expired = allCards.filter((c) => c.isExpired).length;
    const empty = allCards.filter((c) => !c.isExpired && c.remaining === 0).length;
    const low = allCards.filter((c) => !c.isExpired && c.remaining > 0 && c.pct <= 20).length;
    const active = allCards.filter((c) => !c.isExpired && c.remaining > 0).length;
    const totalEntries = allCards.reduce((s, c) => s + c.total, 0);
    const remainingEntries = allCards.reduce((s, c) => s + c.remaining, 0);
    const usedEntries = totalEntries - remainingEntries;
    const utilization = totalEntries > 0 ? Math.round((usedEntries / totalEntries) * 100) : 0;
    return { total, expired, empty, low, active, totalEntries, remainingEntries, usedEntries, utilization };
  }, [allCards]);

  // Featured (most urgent) card — UNPAID cards that already had bookings (trainings used).
  // These are the real admin pain point: card was used but never paid for.
  const featuredCard = useMemo(() => {
    if (allCards.length === 0) return null;
    const candidates = allCards.filter((c) => {
      const isPaid = c.bezahlt || c.bezahlt_am;
      const used = (c.total || 0) - (c.remaining || 0);
      return !isPaid && used > 0;
    });
    if (candidates.length === 0) return null;
    // Prio: most used (= most overdue) first, then expired ones up
    candidates.sort((a, b) => {
      const usedA = (a.total || 0) - (a.remaining || 0);
      const usedB = (b.total || 0) - (b.remaining || 0);
      if (usedA !== usedB) return usedB - usedA;
      if (a.isExpired !== b.isExpired) return a.isExpired ? -1 : 1;
      return a.pct - b.pct;
    });
    return candidates[0];
  }, [allCards]);

  // Filtered cards based on tab/search/status
  const filteredCards = useMemo(() => {
    let list = [...allCards];
    if (statusFilter === 'active') list = list.filter((c) => !c.isExpired && c.remaining > 0);
    else if (statusFilter === 'low') list = list.filter((c) => !c.isExpired && c.remaining > 0 && c.pct <= 20);
    else if (statusFilter === 'expired') list = list.filter((c) => c.isExpired);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (c) =>
          c.cardNumber.toLowerCase().includes(term) ||
          c.ownerName.toLowerCase().includes(term) ||
          c.cardName.toLowerCase().includes(term)
      );
    }
    list.sort((a, b) => {
      if (a.isExpired !== b.isExpired) return a.isExpired ? 1 : -1;
      const ad = a.expiryDate ? new Date(a.expiryDate).getTime() : Infinity;
      const bd = b.expiryDate ? new Date(b.expiryDate).getTime() : Infinity;
      return ad - bd;
    });
    return list;
  }, [allCards, statusFilter, searchTerm]);

  // Top owners by used entries
  const topOwners = useMemo(() => {
    const map = new Map<string, { name: string; initials: string; used: number }>();
    allCards.forEach((c) => {
      const used = c.total - c.remaining;
      if (used <= 0) return;
      const key = c.ownerMgNr;
      const cur = map.get(key);
      if (cur) cur.used += used;
      else map.set(key, { name: c.ownerName, initials: c.ownerInitials, used });
    });
    return Array.from(map.values()).sort((a, b) => b.used - a.used).slice(0, 4);
  }, [allCards]);

  // History timeline — all training bookings across all guests, sorted desc.
  // Primary source: backend /recent-bookings (full event details across all cards)
  // Fallback: per-card bookings nested in guests[] (only loaded after detail-modal open)
  const allHistoryEntries = useMemo(() => {
    type Entry = {
      key: string;
      title: string;
      ownerName: string;
      cardNumber: string;
      bookingDate: string;
      start: string;
      start_time: string;
    };
    if (recentBookings && recentBookings.length > 0) {
      return recentBookings
        .map((b: any) => ({
          key: `rb-${b.booking_id}`,
          title: b.title || 'Training',
          ownerName: `${b.owner_vorname || ''} ${b.owner_name || ''}`.trim(),
          cardNumber: String(b.guest_card_id),
          bookingDate: b.booking_date || b.start || '',
          start: b.start || '',
          start_time: b.start_time || '',
        }))
        .sort((a: Entry, b: Entry) => {
          const ad = a.bookingDate ? new Date(a.bookingDate).getTime() : 0;
          const bd = b.bookingDate ? new Date(b.bookingDate).getTime() : 0;
          return bd - ad;
        });
    }
    // Fallback: aggregate from guest details (loaded only when individual detail modal was opened)
    const list: Entry[] = [];
    guests.forEach((g) => {
      const cards = Array.isArray(g.cards) ? g.cards : [];
      cards.forEach((c: any) => {
        const bookings = c.bookings || [];
        bookings.forEach((b: any, idx: number) => {
          list.push({
            key: `${g.mg_nr}-${c.id}-${b.kalender_id || b.id || idx}`,
            title: b.title || 'Training',
            ownerName: `${g.vorname || ''} ${g.name || ''}`.trim(),
            cardNumber: String(c.id),
            bookingDate: b.booking_date || b.start || '',
            start: b.start || '',
            start_time: b.start_time || '',
          });
        });
      });
    });
    list.sort((a, b) => {
      const ad = a.bookingDate ? new Date(a.bookingDate).getTime() : 0;
      const bd = b.bookingDate ? new Date(b.bookingDate).getTime() : 0;
      return bd - ad;
    });
    return list;
  }, [recentBookings, guests]);

  // Monthly trainings aggregation — prefers extendedStats.monthly, falls back to local
  const monthlyTrainings = useMemo(() => {
    const map: { [key: string]: number } = {};
    if (extendedStats?.monthly && extendedStats.monthly.length > 0) {
      extendedStats.monthly.forEach((item: any) => {
        if (!item?.month) return;
        if (!map[item.month]) map[item.month] = 0;
        map[item.month] += item.total_bookings || 0;
      });
    } else {
      allHistoryEntries.forEach((e) => {
        if (!e.bookingDate) return;
        const d = new Date(e.bookingDate);
        if (isNaN(d.getTime())) return;
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        map[key] = (map[key] || 0) + 1;
      });
    }
    const months = Object.keys(map).sort().reverse();
    const max = Math.max(...Object.values(map), 1);
    const total = Object.values(map).reduce((s, v) => s + v, 0);
    return { months, map, max, total };
  }, [extendedStats, allHistoryEntries]);

  // Daily aggregate fallback (timeSeries) — when per-card bookings aren't loaded yet
  const dailyAggregateEntries = useMemo(() => {
    if (!extendedStats?.timeSeries) return [];
    return extendedStats.timeSeries
      .filter((d: any) => (d?.bookings?.count || 0) > 0)
      .sort((a: any, b: any) => (a.date < b.date ? 1 : -1))
      .map((d: any) => ({
        key: `agg-${d.date}`,
        title: `${d.bookings.count} ${d.bookings.count === 1 ? 'Training' : 'Trainings'}`,
        ownerName:
          d.bookings.unique_users === 1
            ? '1 Gast'
            : `${d.bookings.unique_users} Gäste`,
        cardNumber:
          d.bookings.unique_events === 1
            ? '1 Event'
            : `${d.bookings.unique_events} Events`,
        bookingDate: d.date,
        start: d.date,
        start_time: '',
        isAggregate: true as const,
        dailyCount: d.bookings.count,
      }));
  }, [extendedStats]);

  // Monthly aggregate fallback — when even timeSeries is empty
  const monthlyAggregateEntries = useMemo(() => {
    return monthlyTrainings.months.map((month) => {
      const count = monthlyTrainings.map[month] || 0;
      const [year, monthNum] = month.split('-');
      const d = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
      const monthLabel = d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
      return {
        key: `month-${month}`,
        title: `${count} ${count === 1 ? 'Training' : 'Trainings'} · ${monthLabel}`,
        ownerName: '',
        cardNumber: '',
        bookingDate: `${year}-${monthNum}-01`,
        start: '',
        start_time: '',
        isAggregate: true as const,
        dailyCount: count,
      };
    });
  }, [monthlyTrainings]);

  const historyEntries = useMemo(() => {
    if (allHistoryEntries.length > 0) return allHistoryEntries.slice(0, 20);
    if (dailyAggregateEntries.length > 0) return dailyAggregateEntries.slice(0, 20);
    return monthlyAggregateEntries.slice(0, 20);
  }, [allHistoryEntries, dailyAggregateEntries, monthlyAggregateEntries]);

  const historySource: 'events' | 'days' | 'months' | 'none' = allHistoryEntries.length > 0
    ? 'events'
    : dailyAggregateEntries.length > 0
    ? 'days'
    : monthlyAggregateEntries.length > 0
    ? 'months'
    : 'none';

  const totalHistoryCount =
    allHistoryEntries.length || dailyAggregateEntries.length || monthlyAggregateEntries.length;

  const monthYear = useMemo(
    () => new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }),
    []
  );

  // ============================================================
  // CARD HELPER — color/deg for conic ring
  // ============================================================
  const ringFor = (pct: number, isExpired: boolean) => {
    if (isExpired) return { color: '#475569', deg: 360 };
    const deg = Math.max(0, Math.min(360, (pct / 100) * 360));
    if (pct === 0) return { color: '#f43f5e', deg: 360 };
    if (pct <= 20) return { color: '#f43f5e', deg };
    if (pct <= 50) return { color: '#fbbf24', deg };
    return { color: '#34d399', deg };
  };

  // ============================================================
  // EARLY RETURNS
  // ============================================================
  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center font-sans" style={{ background: 'transparent' }}>
        <div className="text-center">
          <div
            className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
            style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}
          >
            <ExclamationCircleIcon className="h-8 w-8 text-rose-600 dark:text-rose-400" />
          </div>
          <p className="text-lg text-gray-700 dark:text-slate-300">
            Sie haben keine Berechtigung, diese Seite zu sehen.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10 pt-6">
          <div
            className="rounded-3xl p-8 animate-pulse mb-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
          >
            <div className="h-3 w-32 bg-slate-700/60 rounded mb-3" />
            <div className="h-10 w-2/3 bg-slate-700/60 rounded mb-3" />
            <div className="h-4 w-3/4 bg-slate-700/40 rounded" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="rounded-3xl p-6 animate-pulse bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              >
                <div className="h-4 w-24 bg-slate-700/40 rounded mb-2" />
                <div className="h-6 w-40 bg-slate-700/60 rounded mb-5" />
                <div className="flex items-center gap-3">
                  <div className="w-20 h-20 rounded-full bg-slate-700/40" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-3/4 bg-slate-700/60 rounded" />
                    <div className="h-3 w-1/2 bg-slate-700/40 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">

        {/* INTRO: kicker + story + 3 stats */}
        <section className="pt-2 pb-6">
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="text-[11px] uppercase tracking-[0.22em] text-amber-400/80 font-semibold mb-3">
                {cardCounts.active} Gästekarten aktiv · Stand {monthYear}
              </div>
              <h2 className="text-4xl md:text-6xl font-black tracking-tight text-gray-900 dark:text-white leading-[1.02]" style={{ letterSpacing: '-0.04em' }}>
                Gäste-<br /><span className="text-amber-600 dark:text-amber-400">Pool.</span>
              </h2>
              <p className="text-gray-600 dark:text-slate-400 text-base md:text-lg leading-relaxed mt-5 max-w-2xl">
                <span className="font-bold text-gray-900 dark:text-white">{cardCounts.active} {cardCounts.active === 1 ? 'Karte aktiv' : 'Karten aktiv'}</span>
                {cardCounts.low > 0 && (
                  <>
                    {' · '}<span className="font-bold text-amber-700 dark:text-amber-300">{cardCounts.low} fast aufgebraucht</span>
                  </>
                )}
                {cardCounts.expired > 0 && (
                  <>
                    {' · '}<span className="font-bold text-rose-700 dark:text-rose-300">{cardCounts.expired} {cardCounts.expired === 1 ? 'Karte ausgelaufen' : 'Karten ausgelaufen'}</span>
                  </>
                )}
                {cardCounts.usedEntries > 0 && (
                  <>
                    . Bisher haben deine Gäste{' '}
                    <span className="font-bold text-primary-700 dark:text-primary-300">{cardCounts.usedEntries} Trainings</span> eingelöst.
                  </>
                )}
                {cardCounts.usedEntries === 0 && cardCounts.total === 0 && (
                  <>. Noch keine Karten ausgestellt — leg jetzt los.</>
                )}
              </p>
            </div>
            <div className="lg:col-span-4 grid grid-cols-3 gap-3">
              <div className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Aktiv</div>
                <div className="text-2xl lg:text-3xl font-black text-primary-600 dark:text-primary-400 mt-1 tabular-nums">{cardCounts.active}</div>
                <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">{cardCounts.total} gesamt</div>
              </div>
              <div className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Trainings</div>
                <div className="text-2xl lg:text-3xl font-black text-gray-900 dark:text-white mt-1 tabular-nums">{cardCounts.usedEntries}</div>
                <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">eingelöst</div>
              </div>
              <div className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Quote</div>
                <div className="text-2xl lg:text-3xl font-black text-primary-600 dark:text-primary-400 mt-1 tabular-nums">{cardCounts.utilization}%</div>
                <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">Auslastung</div>
              </div>
            </div>
          </div>
        </section>


        {/* HERO: Featured card */}
        {featuredCard && (
          <section className="py-8">
            <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-amber-400/80 font-semibold">Im Fokus · Trainiert &amp; unbezahlt</div>
                {(() => {
                  const used = (featuredCard.total || 0) - (featuredCard.remaining || 0);
                  return (
                    <h3 className="text-2xl md:text-3xl font-black tracking-tight text-gray-900 dark:text-white mt-1">
                      {used} {used === 1 ? 'Training genutzt' : 'Trainings genutzt'} — unbezahlt · #{featuredCard.cardNumber}
                    </h3>
                  );
                })()}
              </div>
              <span
                className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold"
                style={
                  featuredCard.isExpired
                    ? { color: '#fda4af', background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }
                    : featuredCard.pct <= 20
                    ? { color: '#fda4af', background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }
                    : featuredCard.pct <= 50
                    ? { color: '#fcd34d', background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }
                    : { color: '#86efac', background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }
                }
              >
                <span
                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{
                    background: featuredCard.isExpired || featuredCard.pct <= 20 ? '#f43f5e' : featuredCard.pct <= 50 ? '#fbbf24' : '#34d399',
                  }}
                />
                {featuredCard.remaining} {featuredCard.remaining === 1 ? 'Training übrig' : 'Trainings übrig'}
              </span>
            </div>

            <article
              className={`relative rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border shadow-sm dark:shadow-[0_30px_60px_-20px_rgba(0,0,0,0.55)] ${
                featuredCard.pct <= 20
                  ? 'border-rose-300 dark:border-rose-500/40'
                  : featuredCard.pct <= 50
                  ? 'border-amber-300 dark:border-amber-500/40'
                  : 'border-emerald-300 dark:border-emerald-500/40'
              }`}
            >
              <div
                className={`absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl pointer-events-none ${
                  featuredCard.pct <= 20
                    ? 'bg-rose-500/10'
                    : featuredCard.pct <= 50
                    ? 'bg-amber-500/10'
                    : 'bg-emerald-500/10'
                }`}
              />
              <div className="relative px-6 md:px-10 py-8 md:py-10">
                <div className="grid md:grid-cols-[1fr_auto] gap-8 items-center">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span
                        className="inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-semibold tabular-nums bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-slate-700 dark:text-slate-300"
                      >
                        Gästekarte-ID #{featuredCard.cardNumber}
                      </span>
                      <span
                        className="inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-semibold bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300"
                      >
                        {featuredCard.cardName}
                      </span>
                    </div>
                    <h2
                      className="text-gray-900 dark:text-white text-4xl md:text-6xl font-black leading-none"
                      style={{ letterSpacing: '-0.04em' }}
                    >
                      {featuredCard.ownerName}
                    </h2>
                    {(() => {
                      const used = (featuredCard.total || 0) - (featuredCard.remaining || 0);
                      // Resolve card price: try inline price → type price → lookup via cardName in cached cardTypes
                      const cardTypeMatch = cardTypes.find(
                        (t: any) =>
                          t.id === featuredCard.raw?.guest_card_type_id ||
                          t.name === featuredCard.cardName ||
                          t.name === featuredCard.raw?.card_name
                      );
                      const price = Number(
                        featuredCard.raw?.price ||
                          featuredCard.raw?.type?.price ||
                          cardTypeMatch?.price ||
                          0
                      );
                      const isPaid = !!(featuredCard.bezahlt || featuredCard.bezahlt_am);
                      const openAmount = isPaid ? 0 : price;
                      return (
                        <>
                          <div className="mt-5 flex items-baseline gap-3 flex-wrap">
                            <div className="flex items-baseline gap-1">
                              <span
                                className="text-amber-700 dark:text-amber-300 font-black tabular-nums leading-none"
                                style={{ fontSize: 'clamp(2.25rem, 5vw, 3.75rem)', letterSpacing: '-0.04em' }}
                              >
                                {openAmount > 0 ? openAmount.toFixed(2).replace('.', ',') : '0,00'}
                              </span>
                              <span className="text-amber-700/80 dark:text-amber-300/80 text-xl font-black">€</span>
                            </div>
                            <span className="text-[10px] uppercase tracking-widest text-amber-700/80 dark:text-amber-300/70 font-bold">Offen</span>
                          </div>
                          <p className="text-gray-700 dark:text-slate-300 text-sm md:text-base leading-relaxed mt-3 max-w-xl">
                            <span className="font-bold text-gray-900 dark:text-white">{used}</span> von <span className="font-bold text-gray-900 dark:text-white">{featuredCard.total}</span> Trainings genutzt
                            {featuredCard.isExpired ? (
                              <> · <span className="font-bold text-rose-700 dark:text-rose-300">ausgelaufen</span> am {formatDate(featuredCard.expiryDate)}</>
                            ) : featuredCard.expiryDate ? (
                              <> · gültig bis {formatDate(featuredCard.expiryDate)}</>
                            ) : null}
                            {featuredCard.purchaseDate && (
                              <> · ausgestellt {formatDate(featuredCard.purchaseDate)}</>
                            )}
                          </p>
                        </>
                      );
                    })()}
                  </div>

                  {/* CAPACITY RING */}
                  <div className="shrink-0 mx-auto md:mx-0">
                    <div
                      className="relative w-32 h-32 rounded-full"
                      style={{
                        background: `conic-gradient(from 0deg, ${ringFor(featuredCard.pct, featuredCard.isExpired).color} 0deg ${ringFor(featuredCard.pct, featuredCard.isExpired).deg}deg, rgba(148,163,184,0.20) ${ringFor(featuredCard.pct, featuredCard.isExpired).deg}deg 360deg)`,
                        padding: 5,
                      }}
                    >
                      <div
                        className="w-full h-full rounded-full flex flex-col items-center justify-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                      >
                        <div className="text-[9px] uppercase tracking-widest text-gray-600 dark:text-slate-400">Übrig</div>
                        <div className="text-4xl font-black text-gray-900 dark:text-white leading-none tabular-nums">
                          {featuredCard.remaining}
                          <span className="text-lg text-gray-500 dark:text-slate-500 font-bold">/{featuredCard.total}</span>
                        </div>
                        <div
                          className="text-[10px] font-semibold mt-1"
                          style={{ color: ringFor(featuredCard.pct, featuredCard.isExpired).color }}
                        >
                          {Math.round(featuredCard.pct)} % Restwert
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* FOOTER */}
              <div
                className="relative px-6 md:px-10 py-5 md:py-6 grid grid-cols-2 md:grid-cols-6 gap-5 md:gap-6 items-center bg-gray-50 dark:bg-slate-950/40 border-t border-gray-200 dark:border-slate-700"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Gästekarte-ID</div>
                  <div className="text-sm font-bold text-gray-900 dark:text-white mt-1 tabular-nums">#{featuredCard.cardNumber}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Trainings</div>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span
                      className="text-2xl font-black tabular-nums"
                      style={{ color: ringFor(featuredCard.pct, featuredCard.isExpired).color }}
                    >
                      {featuredCard.remaining}
                    </span>
                    <span className="text-[11px] text-gray-600 dark:text-slate-400">/ {featuredCard.total}</span>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Gültig bis</div>
                  <div className="text-sm font-bold text-gray-900 dark:text-white mt-1">
                    {featuredCard.expiryDate ? formatDate(featuredCard.expiryDate) : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Bezahlt</div>
                  <div className={`text-sm font-bold mt-1 ${featuredCard.bezahlt || featuredCard.bezahlt_am ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}`}>
                    {featuredCard.bezahlt || featuredCard.bezahlt_am
                      ? `✓ ${featuredCard.bezahlt_am ? formatDate(featuredCard.bezahlt_am) : 'Bezahlt'}`
                      : 'Offen'}
                  </div>
                </div>
                <div className="col-span-2 md:col-span-2 flex gap-2">
                  <button
                    onClick={() => handleViewDetails(featuredCard.guest)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-2xl text-gray-800 dark:text-slate-200 text-xs font-semibold transition hover:bg-gray-200 dark:hover:bg-white/10 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  >
                    Details
                  </button>
                  {!featuredCard.bezahlt && !featuredCard.bezahlt_am && hasGaestekartenKonto && (
                    <button
                      onClick={() => {
                        setSelectedGuest(featuredCard.guest);
                        setSelectedCard(featuredCard.raw);
                        setShowPaymentModal(true);
                      }}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-2xl text-xs font-bold transition bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300"
                    >
                      Bezahlt markieren
                    </button>
                  )}
                  {(featuredCard.bezahlt || featuredCard.bezahlt_am) && (
                    <button
                      onClick={() => handleUnmarkPaid(featuredCard.raw)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-2xl text-xs font-bold transition bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-700 dark:text-amber-200"
                    >
                      <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
                      Bezahlt-Storno
                    </button>
                  )}
                </div>
              </div>
            </article>
          </section>
        )}

        {/* TABS + SEARCH + FILTER PILLS */}
        <section className="py-6">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4 justify-between">
            <div
              className="inline-flex p-1 rounded-2xl self-start bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
            >
              <button
                onClick={() => setActiveTab('cards')}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition ${
                  activeTab === 'cards' ? 'bg-primary-500 text-slate-950' : 'text-slate-400 hover:text-white'
                }`}
              >
                Karten
                <span
                  className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-black ${
                    activeTab === 'cards' ? 'bg-slate-950/30' : 'bg-white/10'
                  }`}
                >
                  {cardCounts.total}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition ${
                  activeTab === 'history' ? 'bg-primary-500 text-slate-950 font-bold' : 'text-slate-400 hover:text-white'
                }`}
              >
                Verlauf
                <span
                  className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-bold ${
                    activeTab === 'history' ? 'bg-slate-950/30' : 'bg-white/10'
                  }`}
                >
                  {cardCounts.usedEntries}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('stats')}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition ${
                  activeTab === 'stats' ? 'bg-primary-500 text-slate-950 font-bold' : 'text-slate-400 hover:text-white'
                }`}
              >
                Statistik
              </button>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch gap-3 flex-1 lg:max-w-2xl lg:ml-auto">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-slate-500" />
                <input
                  type="text"
                  placeholder="Karten-Nr oder Mitglied suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 rounded-xl text-sm text-gray-900 dark:text-white placeholder-slate-500 focus:outline-none transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center text-gray-600 dark:text-slate-400 hover:text-white hover:bg-white/10 transition"
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {activeTab === 'cards' && (
                <div className="flex gap-2 overflow-x-auto">
                  <button
                    onClick={() => setStatusFilter('all')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition"
                    style={
                      statusFilter === 'all'
                        ? { background: '#34d399', border: '1px solid #34d399', color: '#020617' }
                        : { background: 'rgba(2,6,23,0.55)', border: '1px solid rgba(255,255,255,0.08)', color: '#e2e8f0' }
                    }
                  >
                    Alle · {cardCounts.total}
                  </button>
                  <button
                    onClick={() => setStatusFilter('active')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition"
                    style={
                      statusFilter === 'active'
                        ? { background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }
                        : { background: 'rgba(2,6,23,0.55)', border: '1px solid rgba(255,255,255,0.08)', color: '#cbd5e1' }
                    }
                  >
                    Aktiv · {cardCounts.active}
                  </button>
                  <button
                    onClick={() => setStatusFilter('low')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition"
                    style={
                      statusFilter === 'low'
                        ? { background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.40)', color: '#fcd34d' }
                        : { background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)', color: '#fcd34d' }
                    }
                  >
                    Fast leer · {cardCounts.low}
                  </button>
                  <button
                    onClick={() => setStatusFilter('expired')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition"
                    style={
                      statusFilter === 'expired'
                        ? { background: 'rgba(244,63,94,0.20)', border: '1px solid rgba(244,63,94,0.50)', color: '#fda4af' }
                        : { background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)', color: '#fda4af' }
                    }
                  >
                    Ausgelaufen · {cardCounts.expired}
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* CARD TYPES MANAGEMENT BAR */}

        {activeTab === 'cards' && (
          <section className="pb-3">
            <div
              className="rounded-2xl px-4 sm:px-5 py-3 flex flex-wrap items-center gap-3 bg-white dark:bg-[#1c2432]"
              style={{ border: '1px solid rgba(16,185,129,0.30)', boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.30)' }}
                >
                  <CreditCardIcon className="w-4 h-4 text-primary-700 dark:text-primary-300" />
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900 dark:text-white">{cardTypes.length} Kartentyp{cardTypes.length !== 1 ? 'en' : ''}</div>
                  <div className="text-[11px] text-gray-600 dark:text-slate-400">
                    {cardTypes.slice(0, 3).map((t) => t.name).join(' · ') || 'Noch keine Typen angelegt'}
                  </div>
                </div>
              </div>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                {cardTypes.slice(0, 4).map((cardType) => {
                  const isExpired = cardType.end_date && cardType.end_date !== '9999-12-31' && new Date(cardType.end_date) < new Date();
                  const isUpcoming = new Date(cardType.start_date) > new Date();
                  return (
                    <div
                      key={cardType.id}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold"
                      style={{
                        background: isExpired
                          ? 'rgba(244,63,94,0.10)'
                          : isUpcoming
                          ? 'rgba(59,130,246,0.10)'
                          : 'rgba(255,255,255,0.05)',
                        border: isExpired
                          ? '1px solid rgba(244,63,94,0.30)'
                          : isUpcoming
                          ? '1px solid rgba(59,130,246,0.30)'
                          : '1px solid rgba(255,255,255,0.10)',
                        color: isExpired ? '#fda4af' : isUpcoming ? '#93c5fd' : '#cbd5e1',
                      }}
                    >
                      <TicketIcon className="w-3.5 h-3.5" />
                      <span>{cardType.name}</span>
                      <span className="text-gray-500 dark:text-slate-500">·</span>
                      <span className="tabular-nums">{cardType.total_entries}× · {formatCurrency(cardType.price)}</span>
                      {!isExpired && (
                        <button
                          onClick={() => handleDeactivateCardType(cardType)}
                          className="ml-1 w-5 h-5 rounded flex items-center justify-center text-gray-500 dark:text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition"
                          title="Deaktivieren"
                        >
                          <TrashIcon className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
                <button
                  onClick={() => setShowCreateCardModal(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-xs font-bold transition"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Neuer Typ
                </button>
              </div>
            </div>
          </section>
        )}

        {/* CARD GRID — grouped by member */}
        {activeTab === 'cards' && (() => {
          // Group cards by owner (mg_nr → group)
          const ownerGroups = new Map<string, {
            ownerName: string;
            ownerInitials: string;
            ownerMitgliedNr: string;
            ownerEmail?: string;
            guest: any;
            cards: typeof filteredCards;
          }>();
          filteredCards.forEach((c) => {
            const key = String(c.guest?.mg_nr || c.ownerMitgliedNr || c.ownerName);
            if (!ownerGroups.has(key)) {
              ownerGroups.set(key, {
                ownerName: c.ownerName,
                ownerInitials: c.ownerInitials,
                ownerMitgliedNr: c.ownerMitgliedNr,
                ownerEmail: c.guest?.email,
                guest: c.guest,
                cards: [],
              });
            }
            ownerGroups.get(key)!.cards.push(c);
          });
          // Sort groups: most cards first, then by name
          const groupArr = Array.from(ownerGroups.values()).sort((a, b) => {
            if (b.cards.length !== a.cards.length) return b.cards.length - a.cards.length;
            return a.ownerName.localeCompare(b.ownerName, 'de');
          });
          const totalTicketsInPool = filteredCards.reduce((s, c) => s + (c.total || 0), 0);
          const remainingTicketsInPool = filteredCards.reduce((s, c) => s + (c.remaining || 0), 0);

          return (
          <section className="py-6">
            <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-primary-400/80 font-semibold">Karten nach Mitglied</div>
                <h3 className="text-2xl md:text-3xl font-black tracking-tight text-gray-900 dark:text-white mt-1">
                  {groupArr.length} {groupArr.length === 1 ? 'Mitglied' : 'Mitglieder'} · {filteredCards.length} {filteredCards.length === 1 ? 'Karte' : 'Karten'}
                </h3>
                <p className="text-[11px] text-gray-600 dark:text-slate-400 mt-1">
                  <span className="font-bold text-gray-900 dark:text-white tabular-nums">{remainingTicketsInPool}</span>
                  <span className="text-gray-500 dark:text-slate-500"> / {totalTicketsInPool}</span> Trainings verfügbar
                </p>
              </div>
              <div className="text-[11px] text-gray-500 dark:text-slate-500 hidden sm:block">Gruppiert nach Mitglied · meiste Karten zuerst</div>
            </div>

            {filteredCards.length === 0 ? (
              <div
                className="rounded-3xl p-10 sm:p-14 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
              >
                <div
                  className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5"
                  style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                >
                  <CreditCardIcon className="w-10 h-10 text-primary-600 dark:text-primary-400" />
                </div>
                <h4 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">
                  {searchTerm || statusFilter !== 'all'
                    ? 'Keine Karten passen zum Filter.'
                    : 'Noch keine Gästekarte ausgestellt.'}
                </h4>
                <p className="text-gray-600 dark:text-slate-400 text-base mt-3 max-w-md mx-auto">
                  {searchTerm || statusFilter !== 'all'
                    ? 'Setze die Filter zurück oder ändere den Suchbegriff.'
                    : 'Lege einen Kartentyp an und stelle die erste Gästekarte aus.'}
                </p>
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                  {searchTerm || statusFilter !== 'all' ? (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setStatusFilter('all');
                      }}
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl text-gray-800 dark:text-slate-200 text-sm font-semibold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                    >
                      Filter zurücksetzen
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowCreateCardModal(true)}
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-primary-500 hover:bg-primary-400 text-slate-950 font-bold text-sm transition"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Kartentyp anlegen
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                {groupArr.map((group) => {
                  const groupTotal = group.cards.reduce((s, c) => s + (c.total || 0), 0);
                  const groupRemaining = group.cards.reduce((s, c) => s + (c.remaining || 0), 0);
                  const groupUsed = groupTotal - groupRemaining;
                  const groupPaid = group.cards.filter((c) => c.bezahlt || c.bezahlt_am).length;
                  const groupUnpaid = group.cards.length - groupPaid;
                  const groupExpired = group.cards.filter((c) => c.isExpired).length;
                  const groupAlmostEmpty = group.cards.filter((c) => !c.isExpired && c.pct <= 20 && c.remaining > 0).length;
                  const isDone = (c: any) => (c.bezahlt || c.bezahlt_am) && c.remaining === 0 && !c.isExpired;
                  const doneCards = group.cards.filter(isDone);
                  const liveCards = group.cards.filter((c) => !isDone(c));
                  const collapseDone = doneCards.length >= 2;
                  const displayCards = collapseDone ? liveCards : group.cards;
                  const activity = getInactivityStatus(group.guest);
                  const activityColor =
                    activity.status === 'active' ? '#34d399'
                    : activity.status === 'returning' ? '#fbbf24'
                    : activity.status === 'inactive' ? '#f43f5e'
                    : '#94a3b8';
                  const activityShort =
                    activity.status === 'active' ? 'Aktiv'
                    : activity.status === 'returning' ? 'Rückkehrer'
                    : activity.status === 'inactive' ? 'Inaktiv'
                    : 'Nie aktiv';
                  return (
                    <div
                      key={group.ownerMitgliedNr || group.ownerName}
                      className="rounded-2xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                      style={{ boxShadow: '0 20px 40px -20px rgba(0,0,0,0.45)' }}
                    >
                      {/* Group header — compact, member info + activity + summary */}
                      <div
                        className="px-4 sm:px-5 py-3 flex items-center gap-3"
                        style={{ background: 'rgba(255,255,255,0.015)', borderBottom: '1px solid #1e293b' }}
                      >
                        <div
                          className="w-10 h-10 rounded-lg font-bold flex items-center justify-center text-xs shrink-0 relative"
                          style={{
                            background: 'rgba(16,185,129,0.15)',
                            border: '1px solid rgba(16,185,129,0.30)',
                            color: '#86efac',
                          }}
                        >
                          {group.ownerInitials}
                          <span
                            className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2"
                            style={{ background: activityColor, borderColor: CARD_BG }}
                            title={activityShort}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <h4 className="text-sm font-black text-gray-900 dark:text-white truncate">{group.ownerName}</h4>
                            <span className="inline-flex items-center px-1.5 py-0 rounded-full text-[10px] font-semibold border border-primary-500/30 bg-primary-500/10 text-primary-700 dark:text-primary-300 leading-4">
                              {group.cards.length}
                            </span>
                            {groupUnpaid > 0 && (
                              <span className="inline-flex items-center px-1.5 py-0 rounded-full text-[10px] font-semibold border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300 leading-4">
                                {groupUnpaid} offen
                              </span>
                            )}
                            {groupExpired > 0 && (
                              <span className="inline-flex items-center px-1.5 py-0 rounded-full text-[10px] font-semibold border border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300 leading-4">
                                {groupExpired} ab
                              </span>
                            )}
                            {groupAlmostEmpty > 0 && (
                              <span className="inline-flex items-center px-1.5 py-0 rounded-full text-[10px] font-semibold border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300 leading-4">
                                {groupAlmostEmpty} leer
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-gray-600 dark:text-slate-400 truncate mt-0.5 flex items-center gap-1.5 flex-wrap">
                            <span className="text-gray-500 dark:text-slate-500">MG-{group.ownerMitgliedNr}</span>
                            {group.ownerEmail && (
                              <>
                                <span className="text-slate-600">·</span>
                                <span className="truncate max-w-[180px]" title={group.ownerEmail}>{group.ownerEmail}</span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Activity meta — login, booking, inactivity */}
                        <div className="hidden md:flex items-center gap-4 shrink-0 text-right">
                          <div>
                            <div className="text-[9px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Login</div>
                            <div className="text-[11px] text-gray-700 dark:text-slate-300 tabular-nums whitespace-nowrap">
                              {group.guest?.last_login ? formatDate(group.guest.last_login) : <span className="text-gray-500 dark:text-slate-500 italic">nie</span>}
                            </div>
                          </div>
                          <div>
                            <div className="text-[9px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Buchung</div>
                            <div className="text-[11px] text-gray-700 dark:text-slate-300 tabular-nums whitespace-nowrap">
                              {group.guest?.last_booking ? formatDate(group.guest.last_booking) : <span className="text-gray-500 dark:text-slate-500 italic">nie</span>}
                            </div>
                          </div>
                          <div>
                            <div className="text-[9px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Trainings</div>
                            <div className="text-[11px] font-bold text-gray-900 dark:text-white tabular-nums whitespace-nowrap">
                              {groupRemaining}<span className="text-gray-500 dark:text-slate-500">/{groupTotal}</span>
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => handleViewDetails(group.guest)}
                          className="w-8 h-8 rounded-lg text-primary-700 dark:text-primary-300 hover:bg-primary-500/20 transition flex items-center justify-center shrink-0"
                          style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                          title="Profil"
                        >
                          <EyeIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {/* Mobile activity meta — separate row */}
                      <div
                        className="md:hidden px-4 sm:px-5 py-2 grid grid-cols-3 gap-3 text-[10px]"
                        style={{ borderBottom: '1px solid #1e293b', background: 'rgba(255,255,255,0.01)' }}
                      >
                        <div>
                          <div className="text-gray-500 dark:text-slate-500 uppercase tracking-widest font-bold">Login</div>
                          <div className="text-gray-700 dark:text-slate-300 tabular-nums">
                            {group.guest?.last_login ? formatDate(group.guest.last_login) : <span className="text-gray-500 dark:text-slate-500 italic">nie</span>}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500 dark:text-slate-500 uppercase tracking-widest font-bold">Buchung</div>
                          <div className="text-gray-700 dark:text-slate-300 tabular-nums">
                            {group.guest?.last_booking ? formatDate(group.guest.last_booking) : <span className="text-gray-500 dark:text-slate-500 italic">nie</span>}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500 dark:text-slate-500 uppercase tracking-widest font-bold">Trainings</div>
                          <div className="text-gray-900 dark:text-white font-bold tabular-nums">
                            {groupRemaining}<span className="text-gray-500 dark:text-slate-500">/{groupTotal}</span>
                            <span className="text-gray-500 dark:text-slate-500 ml-1 font-normal">· {groupUsed} genutzt</span>
                          </div>
                        </div>
                      </div>

                      {/* Cards sub-grid — compact */}
                      <div className="p-3 sm:p-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {displayCards.map((card) => {
                  const ring = ringFor(card.pct, card.isExpired);
                  const borderColor = card.isExpired
                    ? 'rgba(244,63,94,0.40)'
                    : card.remaining === 0
                    ? 'rgba(244,63,94,0.40)'
                    : card.pct <= 20
                    ? 'rgba(245,158,11,0.40)'
                    : card.pct >= 100
                    ? 'rgba(16,185,129,0.40)'
                    : '#334155';
                  const status = card.isExpired
                    ? { label: 'Abgelaufen', color: '#fda4af', bg: 'rgba(244,63,94,0.10)', border: 'rgba(244,63,94,0.30)' }
                    : card.remaining === 0
                    ? { label: 'Leer', color: '#fda4af', bg: 'rgba(244,63,94,0.10)', border: 'rgba(244,63,94,0.30)' }
                    : card.pct <= 20
                    ? { label: 'Fast leer', color: '#fcd34d', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.30)' }
                    : card.pct >= 100
                    ? { label: 'Brandneu', color: '#86efac', bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.30)' }
                    : card.pct <= 50
                    ? { label: 'Knapp', color: '#fcd34d', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.30)' }
                    : { label: 'Aktiv', color: '#86efac', bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.30)' };

                  return (
                    <article
                      key={card.id}
                      className="relative rounded-3xl overflow-hidden transition hover:-translate-y-0.5 bg-white dark:bg-[#1c2432]"
                      style={{ border: `1px solid ${borderColor}`, boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)', opacity: card.isExpired ? 0.92 : 1 }}
                    >
                      <div className="p-3.5">
                        <div className="flex items-center gap-3">
                          <div
                            className="relative w-14 h-14 rounded-full shrink-0"
                            style={{
                              background: `conic-gradient(from 0deg, ${ring.color} 0deg ${ring.deg}deg, rgba(255,255,255,0.08) ${ring.deg}deg 360deg)`,
                              padding: 3,
                            }}
                          >
                            <div
                              className="w-full h-full rounded-full flex flex-col items-center justify-center bg-white dark:bg-[#1c2432]"
                              style={{ border: '1px solid #1e293b'  }}
                            >
                              <div
                                className={`text-base font-black leading-none tabular-nums ${
                                  card.remaining === 0 ? 'text-rose-300' : card.pct <= 20 ? 'text-rose-300' : card.pct <= 50 ? 'text-amber-300' : card.isExpired ? 'text-slate-400' : 'text-white'
                                }`}
                              >
                                {card.remaining}
                              </div>
                              <div className="text-[8px] text-gray-600 dark:text-slate-400 font-semibold leading-none mt-0.5">/{card.total}</div>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="text-[9px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold leading-none">Gästekarte-ID</div>
                                <div className={`text-sm font-black tabular-nums tracking-tight mt-0.5 ${card.isExpired ? 'text-slate-300' : 'text-white'} truncate`}>
                                  #{card.cardNumber}
                                </div>
                                <div className={`text-[10px] mt-0.5 truncate ${card.isExpired ? 'text-rose-300' : 'text-slate-400'}`}>
                                  {card.isExpired ? (
                                    <>ab {formatDate(card.expiryDate)}</>
                                  ) : card.expiryDate ? (
                                    <>bis {formatDate(card.expiryDate)}</>
                                  ) : (
                                    <>unbegrenzt</>
                                  )}
                                </div>
                              </div>
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold shrink-0 leading-3"
                                style={{ color: status.color, background: status.bg, border: `1px solid ${status.border}` }}
                              >
                                {(card.pct <= 20 || card.isExpired) && (
                                  <span className="w-1 h-1 rounded-full animate-pulse" style={{ background: status.color }} />
                                )}
                                {status.label}
                              </span>
                            </div>
                            <div className="text-[10px] mt-1.5">
                              {card.bezahlt || card.bezahlt_am ? (
                                <span className="text-emerald-600 dark:text-emerald-400 font-semibold">✓ Bezahlt{card.bezahlt_am ? ` · ${formatDate(card.bezahlt_am)}` : ''}</span>
                              ) : (
                                <span className="text-amber-700 dark:text-amber-300 font-semibold">Offen</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="px-3.5 py-2.5 flex items-center gap-1.5" style={{ borderTop: '1px solid #1e293b' }}>
                        {!card.bezahlt && !card.bezahlt_am && hasGaestekartenKonto ? (
                          <>
                            <button
                              onClick={() => handleDeleteCard(card.raw)}
                              className="w-8 h-8 rounded-xl flex items-center justify-center text-rose-700 dark:text-rose-300 transition hover:bg-rose-500/20"
                              style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}
                              title="Stornieren"
                            >
                              <TrashIcon className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedGuest(card.guest);
                                setSelectedCard(card.raw);
                                setShowPaymentModal(true);
                              }}
                              className="flex-1 px-2 py-2 rounded-xl text-[11px] font-bold transition hover:bg-primary-500/20"
                              style={{
                                background: 'rgba(16,185,129,0.10)',
                                border: '1px solid rgba(16,185,129,0.30)',
                                color: '#86efac',
                              }}
                            >
                              Bezahlen
                            </button>
                          </>
                        ) : !card.bezahlt && !card.bezahlt_am ? (
                          <button
                            onClick={() => handleDeleteCard(card.raw)}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl text-[11px] font-bold transition hover:bg-rose-500/20"
                            style={{
                              background: 'rgba(244,63,94,0.10)',
                              border: '1px solid rgba(244,63,94,0.30)',
                              color: '#fda4af',
                            }}
                          >
                            <TrashIcon className="w-3.5 h-3.5" />
                            Stornieren
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => handleUnmarkPaid(card.raw)}
                              className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl text-[11px] font-semibold transition hover:bg-amber-500/20"
                              style={{
                                background: 'rgba(245,158,11,0.10)',
                                border: '1px solid rgba(245,158,11,0.30)',
                                color: '#fcd34d',
                              }}
                              title="Bezahlt-Markierung stornieren — Karte zurück auf unbezahlt"
                            >
                              <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
                              Storno
                            </button>
                            <button
                              onClick={() => handleDeleteCard(card.raw)}
                              className="w-8 h-8 rounded-xl flex items-center justify-center transition hover:bg-rose-500/20 bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08]"
                              style={{ color: '#94a3b8' }}
                              title="Karte komplett löschen"
                            >
                              <TrashIcon className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </article>
                  );
                        })}
                        {collapseDone && (
                          <article
                            className="relative rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-emerald-500/30"
                            style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
                          >
                            <div className="p-3.5 flex items-center gap-3">
                              <div className="w-14 h-14 rounded-full shrink-0 flex items-center justify-center bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 dark:text-emerald-300">
                                <CheckCircleIcon className="w-7 h-7" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-[9px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold leading-none">Erledigt</div>
                                <div className="text-sm font-black tabular-nums tracking-tight mt-0.5 text-gray-900 dark:text-white">
                                  {doneCards.length} Karten
                                </div>
                                <div className="text-[10px] mt-0.5 text-gray-600 dark:text-slate-400">
                                  bezahlt &amp; vollständig genutzt
                                </div>
                                <div className="text-[10px] mt-1.5 text-emerald-700 dark:text-emerald-300 font-semibold truncate" title={doneCards.map((c) => `#${c.cardNumber}`).join(', ')}>
                                  #{doneCards.map((c) => c.cardNumber).join(' · #')}
                                </div>
                              </div>
                            </div>
                          </article>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
          );
        })()}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <section className="py-6 space-y-6">
            {/* Sub-Stats Bar */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Trainings</div>
                <div className="text-2xl lg:text-3xl font-black text-gray-900 dark:text-white mt-1 tabular-nums">{monthlyTrainings.total}</div>
                <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">gesamt</div>
              </div>
              <div className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Eingelöst</div>
                <div className="text-2xl lg:text-3xl font-black text-primary-600 dark:text-primary-400 mt-1 tabular-nums">{cardCounts.usedEntries}</div>
                <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">/ {cardCounts.totalEntries}</div>
              </div>
              <div className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Top-Gast</div>
                <div className="text-2xl lg:text-3xl font-black text-emerald-600 dark:text-emerald-400 mt-1 tabular-nums">
                  {topOwners[0]?.used ?? 0}
                </div>
                <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5 truncate">{topOwners[0]?.name?.split(' ')[0] || '—'}</div>
              </div>
            </div>

            {/* Monthly trainings chart */}
            {monthlyTrainings.months.length > 0 && (
              <article
                className="rounded-3xl p-6 md:p-7 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
              >
                <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Monatliche Trainings</div>
                    <h4 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                      {monthlyTrainings.total} {monthlyTrainings.total === 1 ? 'Training' : 'Trainings'} · {monthlyTrainings.months.length} {monthlyTrainings.months.length === 1 ? 'Monat' : 'Monate'}
                    </h4>
                    <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">Wie oft Gäste pro Monat trainiert haben.</p>
                  </div>
                  <span
                    className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold"
                    style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }}
                  >
                    {cardCounts.usedEntries} Trainings eingelöst
                  </span>
                </div>
                <div className="space-y-2">
                  {monthlyTrainings.months.slice(0, 12).map((month) => {
                    const count = monthlyTrainings.map[month] || 0;
                    const percentage = (count / monthlyTrainings.max) * 100;
                    const [year, monthNum] = month.split('-');
                    const monthName = new Date(parseInt(year), parseInt(monthNum) - 1).toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
                    return (
                      <div key={month} className="flex items-center gap-3">
                        <span className="text-[11px] text-gray-600 dark:text-slate-400 w-16 tabular-nums">{monthName}</span>
                        <div
                          className="flex-1 h-6 rounded-full relative overflow-hidden bg-gray-100 dark:bg-white/[0.04]"
                        >
                          <div
                            className="absolute left-0 top-0 h-full rounded-full transition-all"
                            style={{
                              width: `${Math.max(percentage, 2)}%`,
                              background: 'linear-gradient(90deg, rgba(16,185,129,0.40) 0%, rgba(16,185,129,0.20) 100%)',
                              borderRight: '1px solid rgba(16,185,129,0.50)',
                            }}
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-900 dark:text-white z-10 tabular-nums">
                            {count}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </article>
            )}

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Timeline */}
              <article
                className="lg:col-span-2 rounded-3xl p-6 md:p-7 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Letzte Trainings</div>
                    <h4 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">Verlauf · {monthYear}</h4>
                    <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">
                      {historySource === 'events' ? (
                        <>Die letzten {historyEntries.length} {historyEntries.length === 1 ? 'Training' : 'Trainings'}{allHistoryEntries.length > historyEntries.length && ` von ${allHistoryEntries.length} insgesamt`} über alle Karten.</>
                      ) : historySource === 'days' ? (
                        <>Aggregierte Tagesübersicht · {totalHistoryCount} aktive {totalHistoryCount === 1 ? 'Tag' : 'Tage'} (Einzelevents beim Klick auf Profil).</>
                      ) : historySource === 'months' ? (
                        <>Monats-Aggregat · {totalHistoryCount} {totalHistoryCount === 1 ? 'Monat' : 'Monate'} mit Trainings (Einzelevents beim Klick auf Profil).</>
                      ) : (
                        'Keine Trainings erfasst.'
                      )}
                    </p>
                  </div>
                  <span
                    className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold"
                    style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }}
                  >
                    {cardCounts.usedEntries} Trainings eingelöst
                  </span>
                </div>

                {historyEntries.length === 0 ? (
                  <div className="text-center py-10">
                    <CalendarIcon className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 dark:text-slate-400">Noch keine Buchungen erfasst.</p>
                  </div>
                ) : (
                  <ul className="relative space-y-1 pl-6">
                    <span
                      className="absolute left-1 top-2 bottom-2 w-px"
                      style={{ background: 'linear-gradient(180deg, rgba(16,185,129,0.50), #475569 50%, transparent)' }}
                    />
                    {historyEntries.map((entry) => (
                      <li
                        key={entry.key}
                        className="relative pl-4 py-3 flex items-center gap-4 rounded-2xl border border-transparent transition hover:bg-white/[0.025]"
                      >
                        <span
                          className="absolute -left-[5px] top-1/2 -translate-y-1/2 rounded-full"
                          style={{
                            width: 10,
                            height: 10,
                            background: '#10b981',
                            boxShadow: '0 0 0 4px rgba(16,185,129,0.12)',
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold"
                              style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }}
                            >
                              {entry.bookingDate ? formatDate(entry.bookingDate) : '—'}
                            </span>
                            <span
                              className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gray-100 dark:bg-slate-950/55 border border-gray-200 dark:border-white/[0.08]"
                              style={{ color: '#cbd5e1'  }}
                            >
                              {entry.cardNumber}
                            </span>
                          </div>
                          <div className="text-sm font-bold text-gray-900 dark:text-white mt-1.5">{entry.title}</div>
                          <div className="text-[11px] text-gray-600 dark:text-slate-400">
                            {entry.start_time && `${entry.start_time} · `}{entry.ownerName}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                            {(entry as any).isAggregate ? 'Trainings' : 'Abzug'}
                          </div>
                          <div className={`text-sm font-black ${(entry as any).isAggregate ? 'text-primary-300' : 'text-rose-300'}`}>
                            {(entry as any).isAggregate ? `${(entry as any).dailyCount}` : '−1'}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </article>

              {/* Top Owners */}
              <article
                className="relative rounded-3xl p-6 overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
              >
                <div
                  className="absolute -top-12 -right-12 w-44 h-44 rounded-full blur-2xl pointer-events-none"
                  style={{ background: 'rgba(16,185,129,0.10)' }}
                />
                <div className="relative">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Top Inhaber</div>
                  <h4 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">Wer nutzt am meisten?</h4>
                  <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">Trainings pro Mitglied · gesamt</p>

                  {topOwners.length === 0 ? (
                    <div className="text-center py-8">
                      <UserGroupIcon className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 dark:text-slate-400">Noch keine Aktivität.</p>
                    </div>
                  ) : (
                    <ul className="mt-5 space-y-3">
                      {topOwners.map((owner, idx) => {
                        const max = topOwners[0].used || 1;
                        const pct = (owner.used / max) * 100;
                        return (
                          <li key={`${owner.name}-${idx}`} className="flex items-center gap-3">
                            <div
                              className="w-9 h-9 rounded-lg font-bold flex items-center justify-center text-xs shrink-0"
                              style={{
                                background: idx === 0 ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.05)',
                                border: idx === 0 ? '1px solid rgba(16,185,129,0.30)' : '1px solid rgba(255,255,255,0.10)',
                                color: idx === 0 ? '#86efac' : '#cbd5e1',
                              }}
                            >
                              {owner.initials}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <div className="text-sm font-bold text-gray-900 dark:text-white truncate">{owner.name}</div>
                                <div className={`text-sm font-black tabular-nums ${idx === 0 ? 'text-primary-300' : 'text-white'}`}>
                                  {owner.used}
                                </div>
                              </div>
                              <div className="mt-1 h-1.5 rounded-full overflow-hidden bg-gray-100 dark:bg-white/5" >
                                <div
                                  className="h-full rounded-full"
                                  style={{ width: `${pct}%`, background: idx === 0 ? '#10b981' : 'rgba(16,185,129,0.6)' }}
                                />
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  <button
                    onClick={() => setActiveTab('stats')}
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-gray-800 dark:text-slate-200 text-sm font-semibold transition hover:bg-white/10 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  >
                    Statistik öffnen
                    <ArrowRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </article>
            </div>
          </section>
        )}

        {/* STATISTICS TAB */}
        {activeTab === 'stats' && (
          <section className="py-6 space-y-8">
            {stats && (() => {
              // Derived metrics for the story
              const paidCardCount = allCards.filter((c) => !!(c.bezahlt || c.bezahlt_am)).length;
              const totalCardCount = allCards.length;
              const paidPct = totalCardCount > 0 ? Math.round((paidCardCount / totalCardCount) * 100) : 0;
              const paidRing = ringFor(paidPct, false);
              const formatMonthShort = (raw: string) => {
                const [y, m] = (raw || '').split('-');
                if (!y || !m) return raw || '—';
                return new Date(parseInt(y), parseInt(m) - 1).toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
              };
              const formatMonthLong = (raw: string) => {
                const [y, m] = (raw || '').split('-');
                if (!y || !m) return raw || '—';
                return new Date(parseInt(y), parseInt(m) - 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
              };

              return (
                <>
                  {/* Sub-Stats Bar */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Karten</div>
                      <div className="text-2xl lg:text-3xl font-black text-gray-900 dark:text-white mt-1 tabular-nums">{stats.totalSoldCards}</div>
                      <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">{stats.activeGuestCards} aktiv</div>
                    </div>
                    <div className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Umsatz</div>
                      <div className="text-2xl lg:text-3xl font-black text-emerald-600 dark:text-emerald-400 mt-1 tabular-nums">
                        {stats.totalRevenue > 9999
                          ? `${(stats.totalRevenue / 1000).toFixed(1)}k€`
                          : formatCurrency(stats.totalRevenue)}
                      </div>
                      <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">{formatCurrency(stats.paidRevenue)} bezahlt</div>
                    </div>
                    <div className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Quote</div>
                      <div className="text-2xl lg:text-3xl font-black text-primary-600 dark:text-primary-400 mt-1 tabular-nums">{paidPct}%</div>
                      <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">bezahlt</div>
                    </div>
                  </div>

                  {/* 2. FEATURED KPI HERO CARD */}
                  <article
                    className="relative rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                    style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
                  >
                    <div
                      className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl pointer-events-none"
                      style={{ background: 'rgba(16,185,129,0.10)' }}
                    />
                    <div className="relative px-6 md:px-10 py-8 md:py-10">
                      <div className="grid md:grid-cols-[1fr_auto] gap-8 items-center">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-4 flex-wrap">
                            <span
                              className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold"
                              style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }}
                            >
                              Gesamtumsatz
                            </span>
                            <span
                              className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                              style={{ color: '#cbd5e1'  }}
                            >
                              {totalCardCount} {totalCardCount === 1 ? 'Karte' : 'Karten'} im System
                            </span>
                          </div>
                          <div className="flex items-baseline gap-3 flex-wrap">
                            <span
                              className="text-emerald-700 dark:text-emerald-300 font-black tabular-nums leading-none"
                              style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', letterSpacing: '-0.04em' }}
                            >
                              {stats.totalRevenue.toFixed(2).replace('.', ',')}
                            </span>
                            <span className="text-emerald-300/80 text-2xl font-black">€</span>
                          </div>
                          <p className="text-gray-700 dark:text-slate-300 text-sm md:text-base leading-relaxed mt-4 max-w-xl">
                            <span className="font-bold text-emerald-700 dark:text-emerald-300">{formatCurrency(stats.paidRevenue)}</span> bereits bezahlt
                            {stats.totalRevenue - stats.paidRevenue > 0 && (
                              <>
                                {' · '}
                                <span className="font-bold text-amber-700 dark:text-amber-300">{formatCurrency(stats.totalRevenue - stats.paidRevenue)}</span> offen
                              </>
                            )}
                            {' · '}
                            Ø <span className="font-bold text-gray-900 dark:text-white">{stats.avgCardsPerGuest}</span> Karten pro Gast.
                          </p>
                        </div>

                        {/* Conic ring: paid / total */}
                        <div className="shrink-0 mx-auto md:mx-0">
                          <div
                            className="relative w-32 h-32 rounded-full"
                            style={{
                              background: `conic-gradient(from 0deg, ${paidRing.color} 0deg ${paidRing.deg}deg, rgba(255,255,255,0.08) ${paidRing.deg}deg 360deg)`,
                              padding: 5,
                            }}
                          >
                            <div
                              className="w-full h-full rounded-full flex flex-col items-center justify-center bg-white dark:bg-[#1c2432]"
                              style={{ border: '1px solid #1e293b'  }}
                            >
                              <div className="text-[9px] uppercase tracking-widest text-gray-600 dark:text-slate-400">Bezahlt</div>
                              <div className="text-3xl font-black text-gray-900 dark:text-white leading-none tabular-nums">
                                {paidPct}<span className="text-base text-gray-500 dark:text-slate-500 font-bold">%</span>
                              </div>
                              <div className="text-[10px] font-semibold mt-1 tabular-nums" style={{ color: paidRing.color }}>
                                {paidCardCount}/{totalCardCount}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </article>

                  {/* 3. MONTHLY CHARTS — 2 col on lg */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Monatliche Verkäufe */}
                    <article
                      className="rounded-3xl p-6 md:p-7 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                      style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                    >
                      <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Monatliche Verkäufe</div>
                          <h4 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">Karten-Absatz</h4>
                          <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">Wie viele Karten wurden pro Monat verkauft.</p>
                        </div>
                        <span
                          className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold"
                          style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }}
                        >
                          {stats.totalSoldCards} {stats.totalSoldCards === 1 ? 'Karte' : 'Karten'} gesamt
                        </span>
                      </div>
                      {(() => {
                        const purchases = stats.monthlyTotalPurchases || {};
                        const months = Object.keys(purchases).sort().reverse().slice(0, 12);
                        if (months.length === 0) {
                          return (
                            <div className="text-center py-10">
                              <ChartBarIcon className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                              <p className="text-sm text-gray-600 dark:text-slate-400">Keine Verkaufsdaten verfügbar.</p>
                            </div>
                          );
                        }
                        const max = Math.max(...months.map((m) => Number(purchases[m] || 0))) || 1;
                        return (
                          <div className="space-y-2">
                            {months.map((month) => {
                              const count = Number(purchases[month] || 0);
                              const pct = (count / max) * 100;
                              return (
                                <div key={month} className="flex items-center gap-3">
                                  <span className="text-[11px] text-gray-600 dark:text-slate-400 w-16 tabular-nums">{formatMonthShort(month)}</span>
                                  <div
                                    className="flex-1 h-6 rounded-full relative overflow-hidden bg-gray-100 dark:bg-white/[0.04]"
                                  >
                                    <div
                                      className="absolute left-0 top-0 h-full rounded-full transition-all"
                                      style={{
                                        width: `${Math.max(pct, 2)}%`,
                                        background: 'linear-gradient(90deg, rgba(16,185,129,0.40) 0%, rgba(16,185,129,0.20) 100%)',
                                        borderRight: '1px solid rgba(16,185,129,0.50)',
                                      }}
                                    />
                                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-900 dark:text-white z-10 tabular-nums">
                                      {count}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </article>

                    {/* Monatliche Trainings */}
                    <article
                      className="rounded-3xl p-6 md:p-7 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                      style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                    >
                      <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Monatliche Trainings</div>
                          <h4 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">Auslastung</h4>
                          <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">Wie oft Gäste pro Monat trainiert haben.</p>
                        </div>
                        <span
                          className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold"
                          style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.30)', color: '#93c5fd' }}
                        >
                          {monthlyTrainings.total} Trainings
                        </span>
                      </div>
                      {(() => {
                        // Prefer extendedStats.monthly aggregated by month; fallback to monthlyTrainings
                        let entries: Array<{ month: string; count: number }> = [];
                        if (extendedStats?.monthly && extendedStats.monthly.length > 0) {
                          const agg: Record<string, number> = {};
                          extendedStats.monthly.forEach((it: any) => {
                            const m = it.month || it.year_month || '';
                            if (!m) return;
                            agg[m] = (agg[m] || 0) + Number(it.total_bookings || it.bookings || 0);
                          });
                          entries = Object.keys(agg)
                            .sort()
                            .reverse()
                            .map((m) => ({ month: m, count: agg[m] }));
                        }
                        if (entries.length === 0 && monthlyTrainings.months.length > 0) {
                          entries = monthlyTrainings.months.map((m) => ({ month: m, count: monthlyTrainings.map[m] || 0 }));
                        }
                        if (entries.length === 0) {
                          return (
                            <div className="text-center py-10">
                              <CalendarIcon className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                              <p className="text-sm text-gray-600 dark:text-slate-400">Keine Trainings erfasst.</p>
                            </div>
                          );
                        }
                        const top = entries.slice(0, 12);
                        const max = Math.max(...top.map((e) => e.count)) || 1;
                        return (
                          <div className="space-y-2">
                            {top.map((entry) => {
                              const pct = (entry.count / max) * 100;
                              return (
                                <div key={entry.month} className="flex items-center gap-3">
                                  <span className="text-[11px] text-gray-600 dark:text-slate-400 w-16 tabular-nums">{formatMonthShort(entry.month)}</span>
                                  <div
                                    className="flex-1 h-6 rounded-full relative overflow-hidden bg-gray-100 dark:bg-white/[0.04]"
                                  >
                                    <div
                                      className="absolute left-0 top-0 h-full rounded-full transition-all"
                                      style={{
                                        width: `${Math.max(pct, 2)}%`,
                                        background: 'linear-gradient(90deg, rgba(59,130,246,0.40) 0%, rgba(59,130,246,0.20) 100%)',
                                        borderRight: '1px solid rgba(59,130,246,0.50)',
                                      }}
                                    />
                                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-900 dark:text-white z-10 tabular-nums">
                                      {entry.count}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </article>
                  </div>

                  {/* 4. KARTENTYPEN-ÜBERSICHT */}
                  {(() => {
                    const types = stats.cardTypes
                      ? Object.entries(stats.cardTypes).map(([id, t]: [string, any]) => ({
                          id,
                          name: t.name || 'Gästekarte',
                          totalEntries: t.total_entries || 0,
                          price: Number(t.price || 0),
                          sold: t.total_count || 0,
                          paid: t.paid_count || 0,
                          booked: t.booked_count || 0,
                          revenue: (t.total_count || 0) * Number(t.price || 0),
                        }))
                      : [];
                    const fallback = (extendedStats?.cardTypes || []).map((t: any, idx: number) => ({
                          id: String(t.id ?? t.card_type_id ?? idx),
                          name: t.name || t.card_type_name || 'Gästekarte',
                          totalEntries: t.total_entries || 0,
                          price: Number(t.price || 0),
                          sold: t.total_count || t.sold_count || t.count || 0,
                          paid: t.paid_count || 0,
                          booked: t.booked_count || 0,
                          revenue: Number(t.total_revenue || (t.total_count || 0) * Number(t.price || 0)),
                        }));
                    const list = types.length > 0 ? types : fallback;
                    if (list.length === 0) return null;
                    const maxRev = Math.max(...list.map((t) => t.revenue)) || 1;
                    return (
                      <article
                        className="rounded-3xl p-6 md:p-7 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                        style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                      >
                        <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Kartentypen</div>
                            <h4 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">Pro Typ · {list.length}</h4>
                            <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">Verkaufte Karten, Trainings und Einnahmen je Typ.</p>
                          </div>
                          <span
                            className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold"
                            style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }}
                          >
                            {formatCurrency(list.reduce((s, t) => s + t.revenue, 0))} gesamt
                          </span>
                        </div>
                        <ul className="space-y-3">
                          {list.map((t) => {
                            const pct = (t.revenue / maxRev) * 100;
                            return (
                              <li
                                key={t.id}
                                className="rounded-2xl p-3 lg:p-4 border border-gray-200 dark:border-white/[0.08]"
                                style={{ background: 'rgba(255,255,255,0.025)' }}
                              >
                                <div className="flex items-center gap-3 flex-wrap">
                                  <div
                                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                                    style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.30)' }}
                                  >
                                    <TicketIcon className="w-5 h-5 text-primary-700 dark:text-primary-300" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h5 className="font-bold text-gray-900 dark:text-white truncate">{t.name}</h5>
                                      <span
                                        className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold tabular-nums bg-gray-100 dark:bg-slate-950/55 border border-gray-200 dark:border-white/[0.08]"
                                        style={{ color: '#cbd5e1'  }}
                                      >
                                        {t.totalEntries || '—'} {t.totalEntries === 1 ? 'Training' : 'Trainings'}
                                      </span>
                                      <span
                                        className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold"
                                        style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)', color: '#fcd34d' }}
                                      >
                                        {formatCurrency(t.price)}
                                      </span>
                                    </div>
                                    <div className="text-[11px] text-gray-600 dark:text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
                                      <span>
                                        <span className="font-bold text-gray-900 dark:text-white tabular-nums">{t.sold}</span> verkauft
                                      </span>
                                      {t.paid > 0 && (
                                        <>
                                          <span className="text-slate-600">·</span>
                                          <span>
                                            <span className="font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">{t.paid}</span> bezahlt
                                          </span>
                                        </>
                                      )}
                                      {t.booked > 0 && (
                                        <>
                                          <span className="text-slate-600">·</span>
                                          <span>
                                            <span className="font-bold text-primary-700 dark:text-primary-300 tabular-nums">{t.booked}</span> gebucht
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Umsatz</div>
                                    <div className="text-base font-black text-emerald-600 dark:text-emerald-400 tabular-nums">{formatCurrency(t.revenue)}</div>
                                  </div>
                                </div>
                                <div
                                  className="mt-3 h-2 rounded-full overflow-hidden bg-gray-100 dark:bg-white/[0.04]"
                                >
                                  <div
                                    className="h-full rounded-full"
                                    style={{
                                      width: `${Math.max(pct, 2)}%`,
                                      background: 'linear-gradient(90deg, rgba(16,185,129,0.40) 0%, rgba(16,185,129,0.20) 100%)',
                                      borderRight: '1px solid rgba(16,185,129,0.50)',
                                    }}
                                  />
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </article>
                    );
                  })()}

                  {/* 5. TOP-MONATE (Umsatz) */}
                  {stats.topMonthsByRevenue && stats.topMonthsByRevenue.length > 0 && (() => {
                    const top = stats.topMonthsByRevenue!.slice(0, 6);
                    const max = Math.max(...top.map((it: any) => Number(it.revenue || 0))) || 1;
                    return (
                      <article
                        className="rounded-3xl p-6 md:p-7 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                        style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                      >
                        <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Top-Monate</div>
                            <h4 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">Stärkster Umsatz</h4>
                            <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">Die sechs umsatzstärksten Monate im Vergleich.</p>
                          </div>
                          <span
                            className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold"
                            style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }}
                          >
                            <ArrowTrendingUpIcon className="w-3.5 h-3.5" />
                            {formatCurrency(top.reduce((s: number, it: any) => s + Number(it.revenue || 0), 0))}
                          </span>
                        </div>
                        <ol className="space-y-2.5">
                          {top.map((item: any, index: number) => {
                            const revenue = Number(item.revenue || 0);
                            const pct = (revenue / max) * 100;
                            const medalBg = index === 0
                              ? 'rgba(245,158,11,0.15)'
                              : index === 1
                              ? 'rgba(148,163,184,0.15)'
                              : index === 2
                              ? 'rgba(245,158,11,0.10)'
                              : 'rgba(255,255,255,0.05)';
                            const medalBorder = index === 0
                              ? 'rgba(245,158,11,0.40)'
                              : index === 1
                              ? 'rgba(148,163,184,0.40)'
                              : index === 2
                              ? 'rgba(245,158,11,0.30)'
                              : 'rgba(255,255,255,0.10)';
                            const medalColor = index === 0 ? '#fcd34d' : index === 1 ? '#cbd5e1' : index === 2 ? '#f59e0b' : '#94a3b8';
                            return (
                              <li
                                key={item.month}
                                className="rounded-2xl p-3 lg:p-4 border border-gray-200 dark:border-white/[0.08]"
                                style={{ background: 'rgba(255,255,255,0.025)' }}
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className="w-9 h-9 rounded-lg flex items-center justify-center font-black shrink-0"
                                    style={{ background: medalBg, border: `1px solid ${medalBorder}`, color: medalColor }}
                                  >
                                    {index + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-gray-900 dark:text-white truncate">{formatMonthLong(item.month)}</div>
                                    <div className="text-[11px] text-gray-600 dark:text-slate-400">
                                      {item.count !== undefined && (
                                        <>
                                          <span className="font-bold text-gray-800 dark:text-slate-200 tabular-nums">{item.count}</span>{' '}
                                          {item.count === 1 ? 'Karte' : 'Karten'}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <div className="text-base font-black text-emerald-600 dark:text-emerald-400 tabular-nums">{formatCurrency(revenue)}</div>
                                  </div>
                                </div>
                                <div
                                  className="mt-3 h-2 rounded-full overflow-hidden bg-gray-100 dark:bg-white/[0.04]"
                                >
                                  <div
                                    className="h-full rounded-full"
                                    style={{
                                      width: `${Math.max(pct, 2)}%`,
                                      background: 'linear-gradient(90deg, rgba(16,185,129,0.40) 0%, rgba(16,185,129,0.20) 100%)',
                                      borderRight: '1px solid rgba(16,185,129,0.50)',
                                    }}
                                  />
                                </div>
                              </li>
                            );
                          })}
                        </ol>
                      </article>
                    );
                  })()}

                  {/* 6. PAYMENT ANALYSIS */}
                  {extendedStats?.paymentAnalysis && extendedStats.paymentAnalysis.length > 0 && (() => {
                    const top = extendedStats!.paymentAnalysis.slice(0, 5);
                    const max = Math.max(...top.map((it: any) => Number(it.count || it.value || 0))) || 1;
                    return (
                      <article
                        className="rounded-3xl p-6 md:p-7 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                        style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                      >
                        <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Zahlungsverhalten</div>
                            <h4 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">Verzögerungen</h4>
                            <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">Wie schnell Karten nach Verkauf bezahlt werden.</p>
                          </div>
                          <span
                            className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold"
                            style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)', color: '#fcd34d' }}
                          >
                            <ClockIcon className="w-3.5 h-3.5" />
                            Top 5
                          </span>
                        </div>
                        <ul className="space-y-2.5">
                          {top.map((item: any, idx: number) => {
                            const label = item.delay_category || item.label || item.name || `Bucket ${idx + 1}`;
                            const count = Number(item.count || item.value || 0);
                            const revenue = Number(item.total_revenue || item.revenue || 0);
                            const pct = (count / max) * 100;
                            return (
                              <li
                                key={`${label}-${idx}`}
                                className="rounded-2xl p-3 lg:p-4 border border-gray-200 dark:border-white/[0.08]"
                                style={{ background: 'rgba(255,255,255,0.025)' }}
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                                    style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.30)' }}
                                  >
                                    <ClockIcon className="w-5 h-5 text-amber-700 dark:text-amber-300" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-gray-900 dark:text-white truncate">{label}</div>
                                    <div className="text-[11px] text-gray-600 dark:text-slate-400 mt-0.5">
                                      <span className="font-bold text-gray-900 dark:text-white tabular-nums">{count}</span> {count === 1 ? 'Karte' : 'Karten'}
                                      {revenue > 0 && (
                                        <>
                                          {' · '}
                                          <span className="font-bold text-emerald-700 dark:text-emerald-300">{revenue > 999 ? `${(revenue / 1000).toFixed(1)}k€` : formatCurrency(revenue)}</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <span
                                    className="rounded-full px-2.5 py-1 text-[11px] font-semibold tabular-nums shrink-0"
                                    style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)', color: '#fcd34d' }}
                                  >
                                    {count}
                                  </span>
                                </div>
                                <div
                                  className="mt-3 h-2 rounded-full overflow-hidden bg-gray-100 dark:bg-white/[0.04]"
                                >
                                  <div
                                    className="h-full rounded-full"
                                    style={{
                                      width: `${Math.max(pct, 2)}%`,
                                      background: 'linear-gradient(90deg, rgba(245,158,11,0.40) 0%, rgba(245,158,11,0.20) 100%)',
                                      borderRight: '1px solid rgba(245,158,11,0.50)',
                                    }}
                                  />
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </article>
                    );
                  })()}

                  {/* 7. RETENTION + UTILIZATION */}
                  {(((extendedStats?.retention?.length ?? 0) > 0) || ((extendedStats?.utilization?.length ?? 0) > 0)) && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {extendedStats?.retention && extendedStats.retention.length > 0 && (
                        <article
                          className="rounded-3xl p-6 md:p-7 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                          style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                        >
                          <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
                            <div>
                              <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Kundenbindung</div>
                              <h4 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">Retention</h4>
                              <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">Wer bleibt aktiv und wer kommt zurück.</p>
                            </div>
                            <UserGroupIcon className="w-5 h-5 text-gray-500 dark:text-slate-500" />
                          </div>
                          <ul className="space-y-2">
                            {extendedStats.retention.map((item: any, idx: number) => {
                              const label = item.customer_type || item.label || item.name || `Gruppe ${idx + 1}`;
                              const count = Number(item.count || item.value || 0);
                              const avgMonths = parseFloat(item.avg_active_months || item.avg_months || '0');
                              return (
                                <li
                                  key={`${label}-${idx}`}
                                  className="flex items-center gap-3 rounded-2xl px-4 py-3 border border-gray-200 dark:border-white/[0.08]"
                                  style={{ background: 'rgba(255,255,255,0.025)' }}
                                >
                                  <div
                                    className="w-9 h-9 rounded-lg font-black flex items-center justify-center text-sm shrink-0 tabular-nums"
                                    style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }}
                                  >
                                    {count}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-gray-900 dark:text-white truncate">{label}</div>
                                    <div className="text-[11px] text-gray-600 dark:text-slate-400">
                                      Ø {avgMonths > 0 ? avgMonths.toFixed(1) : '—'} {avgMonths === 1 ? 'Monat' : 'Monate'} aktiv
                                    </div>
                                  </div>
                                  <span
                                    className="rounded-full px-2.5 py-1 text-[11px] font-semibold tabular-nums shrink-0"
                                    style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }}
                                  >
                                    {count} {count === 1 ? 'Person' : 'Personen'}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </article>
                      )}

                      {extendedStats?.utilization && extendedStats.utilization.length > 0 && (() => {
                        const top = extendedStats!.utilization.slice(0, 6);
                        const maxVal = Math.max(
                          ...top.map((it: any) =>
                            Number(it.utilization_pct || it.utilization || it.avg_used_pct || it.percentage || it.value || it.count || 0)
                          )
                        ) || 1;
                        return (
                          <article
                            className="rounded-3xl p-6 md:p-7 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                            style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                          >
                            <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
                              <div>
                                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Auslastung</div>
                                <h4 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">Utilization</h4>
                                <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">Wie stark Karten im Schnitt genutzt werden.</p>
                              </div>
                              <ChartBarIcon className="w-5 h-5 text-gray-500 dark:text-slate-500" />
                            </div>
                            <ul className="space-y-2">
                              {top.map((item: any, idx: number) => {
                                const label = item.card_type_name || item.name || item.label || `Typ ${idx + 1}`;
                                const value = Number(
                                  item.utilization_pct ||
                                    item.utilization ||
                                    item.avg_used_pct ||
                                    item.percentage ||
                                    item.value ||
                                    item.count ||
                                    0
                                );
                                const pct = (value / maxVal) * 100;
                                const isPercent =
                                  item.utilization_pct !== undefined ||
                                  item.utilization !== undefined ||
                                  item.avg_used_pct !== undefined ||
                                  item.percentage !== undefined;
                                return (
                                  <li
                                    key={`${label}-${idx}`}
                                    className="rounded-2xl px-4 py-3 border border-gray-200 dark:border-white/[0.08]"
                                    style={{ background: 'rgba(255,255,255,0.025)' }}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-bold text-gray-900 dark:text-white truncate">{label}</div>
                                      </div>
                                      <span
                                        className="rounded-full px-2.5 py-1 text-[11px] font-semibold tabular-nums shrink-0"
                                        style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.30)', color: '#93c5fd' }}
                                      >
                                        {value > 0 ? (isPercent ? `${Math.round(value)} %` : value) : '—'}
                                      </span>
                                    </div>
                                    <div
                                      className="mt-2.5 h-2 rounded-full overflow-hidden bg-gray-100 dark:bg-white/[0.04]"
                                    >
                                      <div
                                        className="h-full rounded-full"
                                        style={{
                                          width: `${Math.max(pct, 2)}%`,
                                          background: 'linear-gradient(90deg, rgba(59,130,246,0.40) 0%, rgba(59,130,246,0.20) 100%)',
                                          borderRight: '1px solid rgba(59,130,246,0.50)',
                                        }}
                                      />
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </article>
                        );
                      })()}
                    </div>
                  )}

                  {/* Unpaid bookings warning — preserved as critical alert */}
                  {stats.unpaidBookingMembers && stats.unpaidBookingMembers.length > 0 && (
                    <article
                      className="rounded-3xl p-6 md:p-7 relative overflow-hidden bg-white dark:bg-[#1c2432]"
                      style={{ border: '1px solid rgba(245,158,11,0.30)', boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                    >
                      <div
                        className="absolute -top-12 -right-12 w-44 h-44 rounded-full blur-2xl pointer-events-none"
                        style={{ background: 'rgba(245,158,11,0.10)' }}
                      />
                      <div className="relative flex items-start gap-4">
                        <div
                          className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                          style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.30)' }}
                        >
                          <ExclamationCircleIcon className="w-6 h-6 text-amber-700 dark:text-amber-300" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] uppercase tracking-[0.18em] text-amber-300/90 font-bold">Aktion erforderlich</div>
                          <h4 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-0.5">
                            {stats.unpaidBookingMembers.length} {stats.unpaidBookingMembers.length === 1 ? 'Mitglied' : 'Mitglieder'} unbezahlt &amp; gebucht
                          </h4>
                          <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">Karten wurden bereits genutzt, aber noch nicht bezahlt.</p>
                          <ul className="mt-4 space-y-2">
                            {stats.unpaidBookingMembers.map((member: any) => (
                              <li
                                key={member.member_id}
                                className="flex items-center justify-between text-sm px-3 py-2.5 rounded-xl"
                                style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.20)' }}
                              >
                                <span className="text-gray-800 dark:text-slate-200 truncate mr-2">
                                  <span className="font-bold">{member.vorname} {member.name}</span>
                                  {member.card_types && (
                                    <>
                                      {' '}<span className="text-gray-500 dark:text-slate-500">·</span> <span className="text-gray-600 dark:text-slate-400">{member.card_types}</span>
                                    </>
                                  )}
                                </span>
                                <span
                                  className="rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap"
                                  style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.40)', color: '#fcd34d' }}
                                >
                                  {member.booking_count} {member.booking_count !== 1 ? 'Buchungen' : 'Buchung'}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </article>
                  )}
                </>
              );
            })()}

            {/* Guest list small */}
            <div
              className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
            >
              <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Mitglieder</div>
                  <h4 className="text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">Gäste · {filteredGuests.length}</h4>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowOnlyWithCards(false)}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold transition"
                    style={
                      !showOnlyWithCards
                        ? { background: '#34d399', border: '1px solid #34d399', color: '#020617' }
                        : { background: 'rgba(2,6,23,0.55)', border: '1px solid rgba(255,255,255,0.08)', color: '#cbd5e1' }
                    }
                  >
                    Alle Gäste
                  </button>
                  <button
                    onClick={() => setShowOnlyWithCards(true)}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold transition"
                    style={
                      showOnlyWithCards
                        ? { background: '#34d399', border: '1px solid #34d399', color: '#020617' }
                        : { background: 'rgba(2,6,23,0.55)', border: '1px solid rgba(255,255,255,0.08)', color: '#cbd5e1' }
                    }
                  >
                    Mit Karten
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-3 px-3 pb-2 text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">
                  <button
                    onClick={() => handleSort('name')}
                    className="col-span-5 text-left hover:text-slate-300"
                  >
                    Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                  <div className="col-span-2 text-left hidden sm:block">Karten</div>
                  <div className="col-span-2 text-left hidden sm:block">Tickets</div>
                  <button
                    onClick={() => handleSort('last_booking')}
                    className="col-span-2 text-left hover:text-slate-300"
                  >
                    Aktivität {sortField === 'last_booking' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                  <div className="col-span-1 text-right">Aktion</div>
                </div>

                {filteredGuests.map((guest) => {
                  const cardsArray = Array.isArray(guest.cards) ? guest.cards : [];
                  const totalCards = cardsArray.length;
                  const paidCards = cardsArray.filter((c) => c.bezahlt_am).length;
                  const unpaidCards = totalCards - paidCards;
                  const totalTickets = cardsArray.reduce((s, c) => s + (c.total_entries || 0), 0);
                  const remainingTickets = cardsArray.reduce((s, c) => s + (c.remaining_entries || 0), 0);
                  const usedTickets = totalTickets - remainingTickets;
                  const ticketsPct = totalTickets > 0 ? Math.round((usedTickets / totalTickets) * 100) : 0;
                  const hasUnpaid = unpaidCards > 0;
                  const activity = getInactivityStatus(guest);
                  const activityColor =
                    activity.status === 'active' ? '#34d399' : activity.status === 'returning' ? '#fbbf24' : activity.status === 'inactive' ? '#f43f5e' : '#94a3b8';
                  const activityPillBg =
                    activity.status === 'active' ? 'rgba(16,185,129,0.10)' : activity.status === 'returning' ? 'rgba(245,158,11,0.10)' : activity.status === 'inactive' ? 'rgba(244,63,94,0.10)' : 'rgba(148,163,184,0.10)';
                  const activityPillBorder =
                    activity.status === 'active' ? 'rgba(16,185,129,0.30)' : activity.status === 'returning' ? 'rgba(245,158,11,0.30)' : activity.status === 'inactive' ? 'rgba(244,63,94,0.30)' : 'rgba(148,163,184,0.30)';
                  const activityShortLabel =
                    activity.status === 'active' ? 'Aktiv' : activity.status === 'returning' ? 'Rückkehrer' : activity.status === 'inactive' ? 'Inaktiv' : 'Nie aktiv';

                  return (
                    <div
                      key={guest.mg_nr}
                      className="px-3 sm:px-4 py-3 rounded-2xl transition hover:bg-white/[0.03]"
                      style={{
                        background: hasUnpaid ? 'rgba(245,158,11,0.05)' : 'rgba(255,255,255,0.02)',
                        border: hasUnpaid ? '1px solid rgba(245,158,11,0.20)' : '1px solid rgba(255,255,255,0.05)',
                      }}
                    >
                      {/* Row 1: identity + main metrics */}
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div
                          className="w-10 h-10 rounded-xl font-bold flex items-center justify-center text-xs shrink-0"
                          style={{
                            background: 'rgba(16,185,129,0.15)',
                            border: '1px solid rgba(16,185,129,0.30)',
                            color: '#86efac',
                          }}
                        >
                          {(guest.vorname || ' ').charAt(0)}{(guest.name || ' ').charAt(0)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-sm font-bold text-gray-900 dark:text-white truncate">{guest.vorname} {guest.name}</div>
                            <span
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                              style={{
                                background: activityPillBg,
                                border: `1px solid ${activityPillBorder}`,
                                color: activityColor,
                              }}
                            >
                              <span className="w-1.5 h-1.5 rounded-full" style={{ background: activityColor }} />
                              {activityShortLabel}
                            </span>
                            {hasUnpaid && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                                {unpaidCards} offen
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-gray-600 dark:text-slate-400 truncate mt-0.5 flex items-center gap-2 flex-wrap">
                            <span>MG-{guest.mitglied_nr || guest.mg_nr}</span>
                            {guest.email && (
                              <>
                                <span className="text-slate-600">·</span>
                                <span className="truncate max-w-[200px]" title={guest.email}>{guest.email}</span>
                              </>
                            )}
                            {guest.eintritt && (
                              <>
                                <span className="text-slate-600">·</span>
                                <span title="Beitrittsdatum">seit {formatDate(guest.eintritt)}</span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Karten count — desktop */}
                        <div className="hidden md:block text-right shrink-0 min-w-[64px]">
                          <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Karten</div>
                          {totalCards > 0 ? (
                            <div className="text-sm font-bold text-gray-900 dark:text-white tabular-nums mt-0.5">
                              {totalCards}
                              {paidCards > 0 && <span className="ml-1.5 text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">{paidCards}✓</span>}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500 dark:text-slate-500 mt-0.5">—</div>
                          )}
                        </div>

                        {/* Tickets — desktop */}
                        <div className="hidden lg:block shrink-0 w-32">
                          <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Trainings</div>
                          {totalTickets > 0 ? (
                            <>
                              <div className="text-sm font-bold text-gray-900 dark:text-white tabular-nums mt-0.5">
                                {usedTickets}<span className="text-gray-500 dark:text-slate-500">/{totalTickets}</span>
                              </div>
                              <div className="mt-1 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{
                                    width: `${ticketsPct}%`,
                                    background: ticketsPct >= 80 ? '#f43f5e' : ticketsPct >= 50 ? '#fbbf24' : '#34d399',
                                  }}
                                />
                              </div>
                            </>
                          ) : (
                            <div className="text-sm text-gray-500 dark:text-slate-500 mt-0.5">—</div>
                          )}
                        </div>

                        {/* Action */}
                        <button
                          onClick={() => handleViewDetails(guest)}
                          className="w-9 h-9 rounded-lg text-primary-700 dark:text-primary-300 hover:text-primary-200 hover:bg-primary-500/10 transition flex items-center justify-center shrink-0"
                          title="Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Row 2: activity meta — letzter Login · letzte Buchung */}
                      <div className="mt-2 ml-13 pl-13 sm:pl-14 grid grid-cols-2 sm:grid-cols-3 gap-3 text-[11px]">
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-500 dark:text-slate-500 uppercase tracking-widest text-[9px] font-bold shrink-0">Login</span>
                          <span className="text-gray-700 dark:text-slate-300 truncate">
                            {guest.last_login ? formatDate(guest.last_login) : <span className="text-gray-500 dark:text-slate-500 italic">nie</span>}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-500 dark:text-slate-500 uppercase tracking-widest text-[9px] font-bold shrink-0">Buchung</span>
                          <span className="text-gray-700 dark:text-slate-300 truncate">
                            {guest.last_booking ? formatDate(guest.last_booking) : <span className="text-gray-500 dark:text-slate-500 italic">nie</span>}
                          </span>
                        </div>
                        <div className="hidden sm:flex items-center gap-1.5 col-span-1">
                          <span className="text-gray-500 dark:text-slate-500 uppercase tracking-widest text-[9px] font-bold shrink-0">Inaktiv</span>
                          <span className="text-gray-700 dark:text-slate-300 truncate">{activity.label}</span>
                        </div>
                      </div>

                      {/* Mobile-only metrics */}
                      <div className="md:hidden mt-2 grid grid-cols-2 gap-3 text-[11px] pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                        <div>
                          <div className="text-gray-500 dark:text-slate-500 uppercase tracking-widest text-[9px] font-bold">Karten</div>
                          <div className="text-sm font-bold text-gray-900 dark:text-white tabular-nums mt-0.5">
                            {totalCards > 0 ? (
                              <>
                                {totalCards}
                                {paidCards > 0 && <span className="ml-1.5 text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">{paidCards}✓</span>}
                                {unpaidCards > 0 && <span className="ml-1.5 text-[11px] text-amber-700 dark:text-amber-300 font-semibold">{unpaidCards}!</span>}
                              </>
                            ) : '—'}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500 dark:text-slate-500 uppercase tracking-widest text-[9px] font-bold">Trainings</div>
                          <div className="text-sm font-bold text-gray-900 dark:text-white tabular-nums mt-0.5">
                            {totalTickets > 0 ? <>{usedTickets}<span className="text-gray-500 dark:text-slate-500">/{totalTickets}</span></> : '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </div>

      {/* Guest Details Modal — Story dark style */}
      {showDetailsModal && selectedGuest && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{
            background: 'rgba(2,6,23,0.65)',
            backdropFilter: 'blur(4px)',
            opacity: isModalVisible ? 1 : 0,
            transition: 'opacity 200ms ease',
          }}
          onClick={() => {
            setIsModalVisible(false);
            setTimeout(() => setShowDetailsModal(false), 300);
          }}
        >
          <div
            className="w-full max-h-[92vh] sm:max-w-4xl lg:max-w-5xl flex flex-col rounded-t-3xl sm:rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', transform: isDragging && dragDistance > 0
                ? `translateY(${Math.min(dragDistance, window.innerHeight * 0.5)}px)`
                : isModalVisible ? 'translateY(0)' : 'translateY(100%)', transition: isDragging ? 'transform 0ms' : 'transform 300ms ease' }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => {
              setTouchStart(e.targetTouches[0].clientY);
              setIsDragging(false);
              setDragDistance(0);
            }}
            onTouchMove={(e) => {
              if (!touchStart) return;
              const currentTouch = e.targetTouches[0].clientY;
              const distance = currentTouch - touchStart;
              if (distance > 10) {
                setIsDragging(true);
                setDragDistance(distance);
              }
            }}
            onTouchEnd={(e) => {
              setTouchEnd(e.changedTouches[0].clientY);
              if (dragDistance > 100) {
                setIsModalVisible(false);
                setTimeout(() => setShowDetailsModal(false), 300);
              }
              setIsDragging(false);
              setDragDistance(0);
              setTouchStart(null);
              setTouchEnd(null);
            }}
          >
            {/* Mobile gripper */}
            <div className="sm:hidden sticky top-0 pt-3 pb-2 z-10 touch-none bg-white dark:bg-[#1c2432]" >
              <div className="mx-auto w-16 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.20)' }} />
              <div className="text-center text-[10px] text-gray-500 dark:text-slate-500 mt-1">Nach unten wischen zum Schließen</div>
            </div>

            {/* Header */}
            <div className="px-6 pt-6 pb-4 flex items-start justify-between border-b border-slate-800">
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                <div className="relative shrink-0">
                  <div
                    className="w-14 h-14 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center text-base sm:text-2xl font-black"
                    style={{
                      background: 'rgba(16,185,129,0.15)',
                      border: '1px solid rgba(16,185,129,0.30)',
                      color: '#86efac',
                    }}
                  >
                    {selectedGuest.vorname?.charAt(0)}{selectedGuest.name?.charAt(0)}
                  </div>
                  <div
                    className="absolute -bottom-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center bg-white dark:bg-[#1c2432]"
                    style={{ border: '1px solid rgba(16,185,129,0.40)'  }}
                  >
                    <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">Gast-Profil</div>
                  <h2 className="text-lg sm:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-0.5 truncate">
                    {selectedGuest.vorname} {selectedGuest.name}
                  </h2>
                  <p className="text-gray-600 dark:text-slate-400 text-xs sm:text-sm mt-0.5">
                    MG-{selectedGuest.mitglied_nr || selectedGuest.mg_nr} · seit {selectedGuest.eintritt ? new Date(selectedGuest.eintritt).getFullYear() : '—'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsModalVisible(false);
                  setTimeout(() => setShowDetailsModal(false), 300);
                }}
                className="w-9 h-9 rounded-full flex items-center justify-center transition shrink-0 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                aria-label="Schließen"
              >
                <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-4">
              {/* Aktivität */}
              <div
                className="rounded-2xl overflow-hidden border border-gray-200 dark:border-white/[0.08]"
                style={{ background: 'rgba(255,255,255,0.025)' }}
              >
                <div className="flex items-center gap-2.5 px-5 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <ClockIcon className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                  <span className="text-sm font-bold text-gray-900 dark:text-white">Aktivität</span>
                </div>
                <div className="divide-y divide-white/5">
                  <div className="flex justify-between items-center px-5 py-3">
                    <span className="text-sm text-gray-600 dark:text-slate-400">Letzter Login</span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      {selectedGuest.last_login ? formatDate(selectedGuest.last_login) : 'Nie'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center px-5 py-3">
                    <span className="text-sm text-gray-600 dark:text-slate-400">Letzte Buchung</span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      {selectedGuest.last_booking ? formatDate(selectedGuest.last_booking) : 'Nie'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Gästekarten */}
              <div
                className="rounded-2xl overflow-hidden border border-gray-200 dark:border-white/[0.08]"
                style={{ background: 'rgba(255,255,255,0.025)' }}
              >
                <div className="flex items-center gap-2.5 px-5 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <TicketIcon className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                  <span className="text-sm font-bold text-gray-900 dark:text-white">Gästekarten</span>
                  <span
                    className="ml-auto px-2 py-0.5 rounded-full text-[11px] font-bold bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                    style={{ color: '#cbd5e1'  }}
                  >
                    {guestCards.length}
                  </span>
                </div>

                {guestCards.length > 0 ? (
                  <div className="p-4 space-y-3">
                    {guestCards.map((card) => {
                      const isExpired = !!(card.expiry_date && !isNaN(new Date(card.expiry_date).getTime()) && new Date(card.expiry_date) < new Date());
                      const total = card.total_entries || card.type?.total_entries || 1;
                      const remaining = card.remaining_entries || 0;
                      const pct = (remaining / total) * 100;
                      const ring = ringFor(pct, isExpired);
                      const hasEntries = remaining > 0;
                      const isUnpaid = !card.bezahlt && !card.bezahlt_am;
                      const cardBorder = isExpired
                        ? 'rgba(244,63,94,0.30)'
                        : isUnpaid
                        ? 'rgba(245,158,11,0.30)'
                        : hasEntries
                        ? 'rgba(16,185,129,0.30)'
                        : 'rgba(255,255,255,0.10)';

                      return (
                        <div
                          key={card.id}
                          className="rounded-2xl p-3 lg:p-4"
                          style={{ background: 'rgba(2,6,23,0.40)', border: `1px solid ${cardBorder}` }}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className="relative w-14 h-14 rounded-full shrink-0"
                              style={{
                                background: `conic-gradient(from 0deg, ${ring.color} 0deg ${ring.deg}deg, rgba(255,255,255,0.08) ${ring.deg}deg 360deg)`,
                                padding: 3,
                              }}
                            >
                              <div
                                className="w-full h-full rounded-full flex flex-col items-center justify-center bg-white dark:bg-[#1c2432]"
                                style={{ border: '1px solid #1e293b'  }}
                              >
                                <div
                                  className="text-base font-black leading-none tabular-nums"
                                  style={{ color: ring.color }}
                                >
                                  {remaining}
                                </div>
                                <div className="text-[8px] text-gray-500 dark:text-slate-500">/{total}</div>
                              </div>
                            </div>

                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-black text-gray-900 dark:text-white truncate">
                                {card.card_name || card.type?.name || 'Gästekarte'}
                              </h4>
                              <p className="text-[11px] text-gray-600 dark:text-slate-400">
                                #{card.id} · gekauft {formatDate(card.purchase_date)}
                              </p>
                              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                {isExpired && (
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold"
                                    style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)', color: '#fda4af' }}
                                  >
                                    Abgelaufen
                                  </span>
                                )}
                                {!hasEntries && !isExpired && (
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold"
                                    style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)', color: '#fcd34d' }}
                                  >
                                    Aufgebraucht
                                  </span>
                                )}
                                {(card.bezahlt || card.bezahlt_am) ? (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold"
                                    style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }}
                                  >
                                    <CheckCircleIcon className="w-3 h-3" />
                                    Bezahlt
                                  </span>
                                ) : (
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold"
                                    style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)', color: '#fcd34d' }}
                                  >
                                    Offen
                                  </span>
                                )}
                                {card.expiry_date && !isExpired && (
                                  <span className="text-[10px] text-gray-500 dark:text-slate-500">
                                    bis {formatDate(card.expiry_date)}
                                  </span>
                                )}
                              </div>
                            </div>

                            {isUnpaid && (
                              <div className="flex items-center gap-1.5 shrink-0">
                                <button
                                  onClick={() => {
                                    if (hasGaestekartenKonto) {
                                      setSelectedCard(card);
                                      setShowPaymentModal(true);
                                    }
                                  }}
                                  disabled={!hasGaestekartenKonto}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition disabled:opacity-50 disabled:cursor-not-allowed"
                                  style={{
                                    background: hasGaestekartenKonto ? '#10b981' : 'rgba(255,255,255,0.05)',
                                    color: hasGaestekartenKonto ? '#020617' : '#64748b',
                                    border: hasGaestekartenKonto ? 'none' : '1px solid rgba(255,255,255,0.10)',
                                  }}
                                  title={hasGaestekartenKonto ? 'Als bezahlt markieren' : 'Kein Gästekarten-Konto konfiguriert'}
                                >
                                  <CurrencyEuroIcon className="w-3.5 h-3.5" />
                                  Bezahlt
                                </button>
                                <button
                                  onClick={() => handleDeleteCard(card)}
                                  className="w-8 h-8 rounded-lg flex items-center justify-center text-rose-700 dark:text-rose-300 transition hover:bg-rose-500/20"
                                  style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}
                                  title="Karte löschen"
                                >
                                  <TrashIcon className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <TicketIcon className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 dark:text-slate-400">Keine Gästekarten vorhanden</p>
                  </div>
                )}
              </div>

              {/* Buchungen */}
              <div
                className="rounded-2xl overflow-hidden border border-gray-200 dark:border-white/[0.08]"
                style={{ background: 'rgba(255,255,255,0.025)' }}
              >
                <div className="flex items-center gap-2.5 px-5 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <CalendarIcon className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                  <span className="text-sm font-bold text-gray-900 dark:text-white">Buchungen</span>
                  {selectedGuest.cards && (
                    <span
                      className="ml-auto px-2 py-0.5 rounded-full text-[11px] font-bold bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                      style={{ color: '#cbd5e1'  }}
                    >
                      {selectedGuest.cards.reduce((acc: number, card: any) => acc + (card.bookings?.length || 0), 0)}
                    </span>
                  )}
                </div>

                {selectedGuest.cards && selectedGuest.cards.some((card: any) => card.bookings && card.bookings.length > 0) ? (
                  <div className="p-4 space-y-4">
                    {selectedGuest.cards.map((card: any) =>
                      card.bookings && card.bookings.length > 0 ? (
                        <div key={card.id} className="space-y-3">
                          <h4 className="text-[10px] font-bold text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                            {card.name || card.card_name || 'Gästekarte'} (#{card.id})
                          </h4>
                          {card.bookings.map((booking: any, index: number) => {
                            const eventCardData = {
                              id: booking.kalender_id || booking.id || index,
                              title: booking.title,
                              start: booking.start,
                              start_time: booking.start_time,
                              end_time: booking.end_time || '',
                              creator_name: booking.creator_name || 'Trainer',
                              creator: booking.creator_id || '',
                              creator_image: booking.creator_image,
                              creator_image_updated: booking.creator_image_updated,
                              cotrainer: booking.cotrainer_id || '',
                              cotrainer_name: booking.cotrainer_name || '',
                              cotrainer_image: booking.cotrainer_image,
                              cotrainer_image_updated: booking.cotrainer_image_updated,
                              participantCount: booking.participantCount || 0,
                              max_teilnehmer: booking.max_teilnehmer,
                              farbe: booking.farbe,
                              unique_id: booking.unique_id,
                              booking_date: booking.booking_date,
                              cardName: booking.cardName || card.name,
                              cardId: booking.cardId || card.id,
                              kalender_id: booking.kalender_id,
                            };
                            const cancellationStatus = getCancellationStatus(booking);
                            return (
                              <EventCard
                                key={`${card.id}-${booking.termin_booking_id || booking.kalender_id || booking.id || index}`}
                                event={eventCardData}
                                showCancelButton={true}
                                onCancel={() => handleCancelBooking(booking)}
                                BASE_URL={BASE_URL}
                                isGuestCardStyle={true}
                                cancellationStatus={cancellationStatus}
                              />
                            );
                          })}
                        </div>
                      ) : null
                    )}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <CalendarIcon className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 dark:text-slate-400">Keine Buchungen vorhanden</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            {(() => {
              const hasUnpaid = guestCards.some((c) => !c.bezahlt && !c.bezahlt_am);
              return (
                <div
                  className="px-6 py-5 flex items-center justify-between gap-3"
                  style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
                >
                  {!hasUnpaid ? (
                    <button
                      onClick={handleDeleteGuest}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 dark:text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                      Gast entfernen
                    </button>
                  ) : (
                    <span />
                  )}
                  <button
                    onClick={() => {
                      setIsModalVisible(false);
                      setTimeout(() => setShowDetailsModal(false), 300);
                    }}
                    className="px-5 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 font-bold text-sm transition"
                  >
                    Schließen
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Create Card Type Modal — Story dark style */}
      {showCreateCardModal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowCreateCardModal(false)}
        >
          <div
            className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md flex flex-col overflow-hidden animate-slideUp bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 32px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-6 pb-4 flex items-start justify-between border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                >
                  <CreditCardIcon className="w-5 h-5 text-primary-700 dark:text-primary-300" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">Neuer Kartentyp</div>
                  <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight mt-0.5">Karte konfigurieren</h2>
                </div>
              </div>
              <button
                onClick={() => setShowCreateCardModal(false)}
                className="w-9 h-9 rounded-full flex items-center justify-center transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
              >
                <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
              </button>
            </div>

            <form onSubmit={handleCreateCardType} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Name</label>
                  <input
                    type="text"
                    required
                    value={newCardType.name}
                    onChange={(e) => setNewCardType({ ...newCardType, name: e.target.value })}
                    className="w-full mt-2 px-4 py-3 rounded-xl text-sm font-semibold text-gray-900 dark:text-white placeholder-slate-500 focus:outline-none transition bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-white/10"
                    style={{ colorScheme: 'dark' }}
                    placeholder="z.B. 10er Karte"
                  />
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Anzahl Trainings</label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={newCardType.total_entries}
                    onChange={(e) => setNewCardType({ ...newCardType, total_entries: e.target.value })}
                    className="w-full mt-2 px-4 py-3 rounded-xl text-sm font-semibold text-gray-900 dark:text-white placeholder-slate-500 focus:outline-none transition bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-white/10"
                    style={{ colorScheme: 'dark' }}
                  />
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Preis (€)</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={newCardType.price}
                    onChange={(e) => setNewCardType({ ...newCardType, price: e.target.value })}
                    className="w-full mt-2 px-4 py-3 rounded-xl text-sm font-semibold text-gray-900 dark:text-white placeholder-slate-500 focus:outline-none transition bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-white/10"
                    style={{ colorScheme: 'dark' }}
                    placeholder="z.B. 50.00"
                  />
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Startdatum</label>
                  <input
                    type="date"
                    required
                    value={newCardType.start_date}
                    onChange={(e) => setNewCardType({ ...newCardType, start_date: e.target.value })}
                    className="w-full mt-2 px-4 py-3 rounded-xl text-sm font-semibold text-gray-900 dark:text-white focus:outline-none transition bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-white/10"
                    style={{ colorScheme: 'dark' }}
                  />
                </div>

                <div>
                  <label
                    className="flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer"
                    style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.20)' }}
                  >
                    <input
                      type="checkbox"
                      checked={newCardType.unlimited}
                      onChange={(e) =>
                        setNewCardType({
                          ...newCardType,
                          unlimited: e.target.checked,
                          end_date: e.target.checked ? '' : newCardType.end_date,
                        })
                      }
                      className="w-4 h-4 rounded text-primary-500 focus:ring-primary-500 bg-slate-800 border-slate-600"
                    />
                    <span className="text-sm font-semibold text-gray-800 dark:text-slate-200">Unbeschränkt gültig (kein Enddatum)</span>
                  </label>

                  {!newCardType.unlimited && (
                    <div className="mt-3">
                      <label className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Enddatum</label>
                      <input
                        type="date"
                        required={!newCardType.unlimited}
                        value={newCardType.end_date}
                        onChange={(e) => setNewCardType({ ...newCardType, end_date: e.target.value })}
                        className="w-full mt-2 px-4 py-3 rounded-xl text-sm font-semibold text-gray-900 dark:text-white focus:outline-none transition bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-white/10"
                        style={{ colorScheme: 'dark' }}
                        min={newCardType.start_date}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div
                className="px-6 py-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"
                style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
              >
                <button
                  type="button"
                  onClick={() => setShowCreateCardModal(false)}
                  className="px-4 py-3 rounded-xl text-gray-800 dark:text-slate-200 text-sm font-semibold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-sm font-bold transition"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  Erstellen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal — Story dark style */}
      {showPaymentModal && selectedCard && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
          onClick={() => {
            setShowPaymentModal(false);
            setSelectedCard(null);
          }}
        >
          <div
            className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md flex flex-col overflow-hidden animate-slideUp bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 32px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-6 pb-4 flex items-start justify-between border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                >
                  <CurrencyEuroIcon className="w-5 h-5 text-primary-700 dark:text-primary-300" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">Zahlung erfassen</div>
                  <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight mt-0.5">Als bezahlt markieren</h2>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedCard(null);
                }}
                className="w-9 h-9 rounded-full flex items-center justify-center transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
              >
                <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleMarkPaid();
              }}
            >
              <div className="p-6 space-y-4">
                <div
                  className="px-4 py-3 rounded-xl flex items-center gap-3 border border-gray-200 dark:border-white/[0.08]"
                  style={{ background: 'rgba(255,255,255,0.025)' }}
                >
                  <TicketIcon className="w-4 h-4 text-gray-600 dark:text-slate-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-gray-900 dark:text-white truncate">
                      {selectedCard.card_name || selectedCard.type?.name || 'Gästekarte'}
                    </div>
                    <div className="text-[11px] text-gray-600 dark:text-slate-400">#{selectedCard.id}</div>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Zahlungsdatum</label>
                  <input
                    type="date"
                    required
                    value={paymentDetails.bezahlt_am}
                    onChange={(e) => setPaymentDetails({ ...paymentDetails, bezahlt_am: e.target.value })}
                    className="w-full mt-2 px-4 py-3 rounded-xl text-sm font-semibold text-gray-900 dark:text-white focus:outline-none transition bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-white/10"
                    style={{ colorScheme: 'dark' }}
                  />
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Beleg (optional)</label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) =>
                      setPaymentDetails({
                        ...paymentDetails,
                        document: e.target.files ? e.target.files[0] : null,
                      })
                    }
                    className="w-full mt-2 px-4 py-3 rounded-xl text-sm text-gray-800 dark:text-slate-200 focus:outline-none transition file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-primary-500 file:text-slate-950 hover:file:bg-primary-400 bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-white/10"
                  />
                </div>
              </div>

              <div
                className="px-6 py-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"
                style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedCard(null);
                  }}
                  className="px-4 py-3 rounded-xl text-gray-800 dark:text-slate-200 text-sm font-semibold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-sm font-bold transition"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  Als bezahlt markieren
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        visible={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant || 'single'}
        showCloseButton={true}
        button={
          confirmModal.variant === 'single'
            ? {
                text: 'OK',
                onPress: () => setConfirmModal({ ...confirmModal, isOpen: false }),
              }
            : undefined
        }
        buttons={
          confirmModal.variant === 'two'
            ? {
                cancel: {
                  text: 'Abbrechen',
                  onPress: () => setConfirmModal({ ...confirmModal, isOpen: false }),
                },
                confirm: {
                  text: 'Bestätigen',
                  onPress: () => {
                    if (confirmModal.onConfirm) {
                      confirmModal.onConfirm();
                    }
                    setConfirmModal({ ...confirmModal, isOpen: false });
                  },
                },
              }
            : undefined
        }
      />

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slideUp { animation: slideUp 0.2s ease; }
      `}</style>
    </div>
  );
};

export default GuestCardManagement;
